import { Fragment, useEffect, useMemo, useState } from 'react';
import { Header } from '@/components';
import { useAppStore } from '@/stores/appStore';
import {
  createUserAsAdmin,
  addCreditsAsAdmin,
  getActivityAsAdmin,
  getCreditUsageAsAdmin,
  getUserAsAdmin,
  listUsersAsAdmin,
  setCreditsAsAdmin,
  setDisabledAsAdmin,
  setPasswordAsAdmin,
  setWebhookAsAdmin,
  setGhlConfigAsAdmin,
  setGhlLocationAssignmentsAsAdmin,
  setFeaturesAsAdmin,
  setInstantlyCampaignAssignmentsAsAdmin,
  setUserTypeAsAdmin,
  listWebhookInboundLogsAsAdmin,
  listWebhookOutboundLogsAsAdmin,
  listErrorLogsAsAdmin,
  listSchedulesAsAdmin,
  listScheduleRunsAsAdmin,
  updateScheduleAsAdmin,
  deleteScheduleAsAdmin,
  listWebsiteRequests,
  updateWebsiteRequest,
  listTestDriveRequests,
  updateTestDriveRequest,
  type AdminUser,
  type GhlAccountRecord,
  type CreditUsageSummary,
  type WebsiteRequest,
  listGhlAccountsAsAdmin,
  upsertGhlAccountAsAdmin,
  testGhlAccountAsAdmin,
  provisionGhlCustomFieldsAsAdmin,
  setDailyLimitsAsAdmin,
  listInstantlyMetricsAsAdmin,
  syncInstantlyMetricsAsAdmin,
  upsertInstantlyReplyAsAdmin,
  deleteInstantlyReplyAsAdmin,
  type InstantlyCampaignMetric,
  type InstantlyDayMetric,
  type InstantlyPositiveReply,
  type InstantlySenderMetric,
} from '@/services/admin';
import { API_CONFIG } from '@/services/api';
import { GHL_REQUIRED_CUSTOM_FIELDS } from '@/data/ghlRequiredCustomFields';
import { listInstantlyCampaignOptions, type InstantlyCampaignOption } from '@/services/research';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

type ActivityItem = {
  id?: string;
  clientId?: string;
  amount?: number;
  source?: string;
  createdAt?: string;
  [key: string]: any;
};

type LogItem = {
  createdAt?: string;
  endpoint?: string;
  accountId?: string;
  searchId?: string;
  responseStatus?: string;
  message?: string;
  [key: string]: any;
};

type CreditUserScheduleItem = {
  userEmail: string;
  scheduleId: string;
  name: string;
  status: string;
  scheduleDays: string[];
  scheduleHour: number | null;
  scheduleMinute: number | null;
  timezone: string;
  nextRunAt: string | null;
  updatedAt: string | null;
  creditLimit: number | null;
  _env: 'prod' | 'dev';
};

function formatScheduleTime(hour: number | null | undefined, minute: number | null | undefined): string {
  if (typeof hour !== 'number' || !Number.isFinite(hour)) return '—';
  const safeHour = Math.max(0, Math.min(23, Math.floor(hour)));
  const safeMinute = typeof minute === 'number' && Number.isFinite(minute) ? Math.max(0, Math.min(59, Math.floor(minute))) : 0;
  return `${String(safeHour).padStart(2, '0')}:${String(safeMinute).padStart(2, '0')}`;
}

export function CommandCenter() {
  const { userEmail, adminToken, isAdmin } = useAppStore();

  const [targetEmail, setTargetEmail] = useState('');
  const [user, setUser] = useState<AdminUser | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'error' | 'success' | 'info'; message: string } | null>(null);

  const [creditAdd, setCreditAdd] = useState('');
  const [creditSet, setCreditSet] = useState('');
  const [contactCreditAdd, setContactCreditAdd] = useState('');
  const [contactCreditSet, setContactCreditSet] = useState('');
  const [dailyCreditLimitInput, setDailyCreditLimitInput] = useState('');
  const [dailyContactCreditLimitInput, setDailyContactCreditLimitInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [webhookInput, setWebhookInput] = useState('');
  const [ghlPitInput, setGhlPitInput] = useState('');
  const [ghlLocationIdInput, setGhlLocationIdInput] = useState('');
  const [linkedinCookieInput, setLinkedinCookieInput] = useState('');
  const [instagramCookieInput, setInstagramCookieInput] = useState('');
  const [linkedinCommentAgentInput, setLinkedinCommentAgentInput] = useState('');
  const [instagramCommentAgentInput, setInstagramCommentAgentInput] = useState('');
  const [pbConnectorIdLiInput, setPbConnectorIdLiInput] = useState('');
  const [pbConnectorIdIgInput, setPbConnectorIdIgInput] = useState('');
  const [activityLimit, setActivityLimit] = useState('50');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createCredits, setCreateCredits] = useState('200');
  const [createTimezone, setCreateTimezone] = useState('America/Chicago');
  const [createIsAdmin, setCreateIsAdmin] = useState(false);
  const [createUserType, setCreateUserType] = useState<'user' | 'admin' | 'free-trial'>('user');
  const [createDisabled, setCreateDisabled] = useState(false);
  const [userList, setUserList] = useState<AdminUser[]>([]);
  const [listSearch, setListSearch] = useState('');
  const [listSort, setListSort] = useState<'email' | 'credits' | 'lastLoginAt' | 'scheduleCount'>('credits');
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('desc');

  const [logTab, setLogTab] = useState<'inbound' | 'outbound' | 'errors' | 'website_requests'>('inbound');
  const [logLimit, setLogLimit] = useState('100');
  const [logSearch, setLogSearch] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [logStatus, setLogStatus] = useState<string | null>(null);
  const [webhookInbound, setWebhookInbound] = useState<LogItem[]>([]);
  const [webhookOutbound, setWebhookOutbound] = useState<LogItem[]>([]);
  const [errorLogs, setErrorLogs] = useState<LogItem[]>([]);
  const [websiteRequests, setWebsiteRequests] = useState<WebsiteRequest[]>([]);
  const [websiteDrafts, setWebsiteDrafts] = useState<Record<string, { emailSentDate: string; pdfUrl: string }>>({});

  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState<string | null>(null);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [scheduleSort, setScheduleSort] = useState<'nextRunAt' | 'createdAt' | 'userEmail'>('nextRunAt');
  const [scheduleSortDir, setScheduleSortDir] = useState<'asc' | 'desc'>('desc');
  const [schedules, setSchedules] = useState<any[]>([]);
  const [scheduleEnv, setScheduleEnv] = useState<'prod' | 'dev' | 'all'>('prod');
  const [scheduleAdminPassword, setScheduleAdminPassword] = useState('');
  const [scheduleRunTab, setScheduleRunTab] = useState<'runs' | 'errors'>('runs');
  const [scheduleRunLoading, setScheduleRunLoading] = useState(false);
  const [scheduleRunStatus, setScheduleRunStatus] = useState<string | null>(null);
  const [scheduleRuns, setScheduleRuns] = useState<any[]>([]);

  const [featureCustomKpi, setFeatureCustomKpi] = useState(true);
  const [featureContentScraping, setFeatureContentScraping] = useState(true);
  const [featureProfitPod, setFeatureProfitPod] = useState(true);
  const [featureBusinessIntel, setFeatureBusinessIntel] = useState(true);
  const [featurePepaiResearch, setFeaturePepaiResearch] = useState(false);
  const [featureEmailWorkspace, setFeatureEmailWorkspace] = useState(false);
  const [featureStatus, setFeatureStatus] = useState<string | null>(null);
  const [userTypeChoice, setUserTypeChoice] = useState<'user' | 'admin' | 'free-trial'>('user');
  const [userTypeStatus, setUserTypeStatus] = useState<string | null>(null);
  const [sectionTab, setSectionTab] = useState<'ghl' | 'instantly' | 'credit_usage' | 'credit_users' | 'all'>('ghl');
  const [ghlAccounts, setGhlAccounts] = useState<GhlAccountRecord[]>([]);
  const [ghlSearch, setGhlSearch] = useState('');
  const [ghlAccountLocationIdInput, setGhlAccountLocationIdInput] = useState('');
  const [ghlAccountPitInput, setGhlAccountPitInput] = useState('');
  const [ghlAccountNameInput, setGhlAccountNameInput] = useState('');
  const [ghlAccountEmailInput, setGhlAccountEmailInput] = useState('');
  const [ghlStatus, setGhlStatus] = useState<string | null>(null);
  const [ghlSaving, setGhlSaving] = useState(false);
  const [ghlTesting, setGhlTesting] = useState(false);
  const [testContactName, setTestContactName] = useState('Profitibull Support');
  const [testContactEmail, setTestContactEmail] = useState('support@profitibull.com');
  const [testCompanyName, setTestCompanyName] = useState('Profitibull');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [ghlFieldProvisioning, setGhlFieldProvisioning] = useState(false);
  const [ghlFieldNamesInput, setGhlFieldNamesInput] = useState(GHL_REQUIRED_CUSTOM_FIELDS.join('\n'));
  const [ghlFieldProvisionStatus, setGhlFieldProvisionStatus] = useState<string | null>(null);
  const [ghlFieldProvisionResult, setGhlFieldProvisionResult] = useState<string | null>(null);
  const [instantlyLoading, setInstantlyLoading] = useState(false);
  const [instantlyStatus, setInstantlyStatus] = useState<string | null>(null);
  const [instantlyFilterUserEmail, setInstantlyFilterUserEmail] = useState('');
  const [instantlyCampaignOptions, setInstantlyCampaignOptions] = useState<InstantlyCampaignOption[]>([]);
  const [instantlyAssignmentSelectedIds, setInstantlyAssignmentSelectedIds] = useState<string[]>([]);
  const [instantlyAssignmentLoading, setInstantlyAssignmentLoading] = useState(false);
  const [instantlyAssignmentStatus, setInstantlyAssignmentStatus] = useState<string | null>(null);
  const [ghlAssignmentSelectedIds, setGhlAssignmentSelectedIds] = useState<string[]>([]);
  const [ghlAssignmentDefaultLocationId, setGhlAssignmentDefaultLocationId] = useState('');
  const [ghlAssignmentSaving, setGhlAssignmentSaving] = useState(false);
  const [ghlAssignmentStatus, setGhlAssignmentStatus] = useState<string | null>(null);
  const [instantlyFilterCampaignId, setInstantlyFilterCampaignId] = useState('__all__');
  const [instantlyFilterSenderEmail, setInstantlyFilterSenderEmail] = useState('');
  const [instantlyFilterStart, setInstantlyFilterStart] = useState('');
  const [instantlyFilterEnd, setInstantlyFilterEnd] = useState('');
  const [instantlyLimit, setInstantlyLimit] = useState('20000');
  const [instantlyCampaignRows, setInstantlyCampaignRows] = useState<InstantlyCampaignMetric[]>([]);
  const [instantlySenderRows, setInstantlySenderRows] = useState<InstantlySenderMetric[]>([]);
  const [instantlyDayRows, setInstantlyDayRows] = useState<InstantlyDayMetric[]>([]);
  const [instantlyPositiveReplies, setInstantlyPositiveReplies] = useState<InstantlyPositiveReply[]>([]);
  const [instantlyTotals, setInstantlyTotals] = useState<Record<string, number>>({});
  const [instantlyUniqueTotals, setInstantlyUniqueTotals] = useState<Record<string, number>>({});
  const [selectedReplyKey, setSelectedReplyKey] = useState<string | null>(null);
  const [replyDraftMessage, setReplyDraftMessage] = useState('');
  const [replyDraftNote, setReplyDraftNote] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [creditUsageLoading, setCreditUsageLoading] = useState(false);
  const [creditUsageStatus, setCreditUsageStatus] = useState<string | null>(null);
  const [creditUsage, setCreditUsage] = useState<CreditUsageSummary | null>(null);
  const [creditUserSearch, setCreditUserSearch] = useState('');
  const [creditUserSchedulesStatus, setCreditUserSchedulesStatus] = useState<string | null>(null);
  const [creditUserSchedules, setCreditUserSchedules] = useState<CreditUserScheduleItem[]>([]);
  const [creditUserSchedulesLoadingByEmail, setCreditUserSchedulesLoadingByEmail] = useState<Record<string, boolean>>({});
  const [creditUserSchedulesLoadedByEmail, setCreditUserSchedulesLoadedByEmail] = useState<Record<string, boolean>>({});
  const [expandedCreditUsers, setExpandedCreditUsers] = useState<Record<string, boolean>>({});

  const adminReady = useMemo(() => Boolean(userEmail && adminToken), [userEmail, adminToken]);
  const websiteRequestsReady = useMemo(
    () => Boolean(adminReady && (isAdmin || userEmail.trim().toLowerCase().endsWith('@profitibull.com'))),
    [adminReady, isAdmin, userEmail]
  );
  const filteredCreditUsers = useMemo(() => {
    if (!creditUsage) return [];
    const q = creditUserSearch.trim().toLowerCase();
    const rows = q
      ? creditUsage.users.filter((item) => item.email.toLowerCase().includes(q))
      : creditUsage.users;
    return [...rows].sort(
      (a, b) =>
        b.seamlessCreditsMonthToDate - a.seamlessCreditsMonthToDate ||
        b.creditsMonthToDate - a.creditsMonthToDate ||
        b.creditsAllTime - a.creditsAllTime
    );
  }, [creditUsage, creditUserSearch]);
  const creditUserSchedulesByEmail = useMemo(() => {
    const byEmail = new Map<string, CreditUserScheduleItem[]>();
    for (const schedule of creditUserSchedules) {
      const email = String(schedule.userEmail || '').trim().toLowerCase();
      if (!email) continue;
      const list = byEmail.get(email) ?? [];
      list.push(schedule);
      byEmail.set(email, list);
    }
    for (const [email, list] of byEmail.entries()) {
      list.sort((a, b) => {
        const aTime = Date.parse(String(a.nextRunAt ?? '')) || 0;
        const bTime = Date.parse(String(b.nextRunAt ?? '')) || 0;
        return aTime - bTime;
      });
      byEmail.set(email, list);
    }
    return byEmail;
  }, [creditUserSchedules]);

  useEffect(() => {
    if (!isAdmin && websiteRequestsReady && logTab !== 'website_requests') {
      setLogTab('website_requests');
    }
  }, [isAdmin, logTab, websiteRequestsReady]);

  useEffect(() => {
    if (sectionTab !== 'ghl' || !adminReady) return;
    if (ghlAccounts.length > 0) return;
    loadGhlAccounts();
  }, [sectionTab, adminReady]);

  useEffect(() => {
    if (sectionTab !== 'instantly' || !adminReady) return;
    if (instantlyCampaignRows.length > 0 || instantlyDayRows.length > 0) return;
    void loadInstantlyMetrics();
  }, [sectionTab, adminReady, instantlyCampaignRows.length, instantlyDayRows.length]);

  useEffect(() => {
    if (sectionTab !== 'instantly') return;
    let cancelled = false;
    loadInstantlyCampaignOptionsForAssignments(true)
      .then((campaigns) => {
        if (cancelled) return;
        setInstantlyCampaignOptions(campaigns);
      })
      .catch(() => {
        if (cancelled) return;
        setInstantlyCampaignOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sectionTab]);

  useEffect(() => {
    if ((sectionTab !== 'credit_usage' && sectionTab !== 'credit_users') || !adminReady) return;
    if (creditUsage) return;
    void loadCreditUsage();
  }, [sectionTab, adminReady, creditUsage]);

  const normalizeCampaignIds = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const next: string[] = [];
    for (const value of raw) {
      const id = typeof value === 'string' ? value.trim() : '';
      if (!id || seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }
    return next;
  };

  const normalizeLocationIds = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const next: string[] = [];
    for (const value of raw) {
      const id = typeof value === 'string' ? value.trim() : '';
      if (!id || seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }
    return next;
  };

  const loadInstantlyCampaignOptionsForAssignments = async (force = false) => {
    if (!force && instantlyCampaignOptions.length > 0) return instantlyCampaignOptions;
    try {
      const result = await listInstantlyCampaignOptions();
      const campaigns = Array.isArray(result.campaigns) ? result.campaigns : [];
      setInstantlyCampaignOptions(campaigns);
      return campaigns;
    } catch {
      if (force) setInstantlyCampaignOptions([]);
      return [];
    }
  };

  const loadUserList = async () => {
    if (!adminReady) return;
    setLoading(true);
    setStatus(null);
    try {
      const result = await listUsersAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        limit: 200,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load users.');
      setUserList(result.users ?? []);
      setStatus({ type: 'success', message: 'User list loaded.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'User list failed.' });
    } finally {
      setLoading(false);
    }
  };

  const loadCreditUsage = async () => {
    if (!adminReady) return;
    setCreditUsageLoading(true);
    setCreditUsageStatus(null);
    try {
      const result = await getCreditUsageAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        baseUrl: API_CONFIG.USERS_API,
      });
      if (!result.success || !result.usage) throw new Error('Failed to load credit usage.');
      setCreditUsage(result.usage);
      setCreditUsageStatus(`Updated ${new Date(result.usage.generatedAt).toLocaleString()}`);
    } catch (error: any) {
      setCreditUsageStatus(error?.message || 'Failed to load credit usage.');
    } finally {
      setCreditUsageLoading(false);
    }
  };

  const listSchedulesForCreditUser = async (targetEmail: string, baseUrl: string, env: 'prod' | 'dev') => {
    const all: CreditUserScheduleItem[] = [];
    let startKey: any = undefined;
    for (let page = 0; page < 10; page += 1) {
      const result = await listSchedulesAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        requesterPassword: scheduleAdminPassword || undefined,
        baseUrl,
        limit: 200,
        filterEmail: targetEmail,
        startKey,
      });
      if (!result.success) {
        throw new Error(result.error || `Failed to load ${env} schedules.`);
      }
      const items = Array.isArray(result.items) ? result.items : [];
      for (const raw of items) {
        all.push({
          userEmail: String(raw?.userEmail ?? '').trim().toLowerCase(),
          scheduleId: String(raw?.scheduleId ?? ''),
          name: String(raw?.name ?? ''),
          status: String(raw?.status ?? 'ACTIVE'),
          scheduleDays: Array.isArray(raw?.scheduleDays) ? raw.scheduleDays.map((d: unknown) => String(d ?? '')) : [],
          scheduleHour: Number.isFinite(Number(raw?.scheduleHour)) ? Number(raw.scheduleHour) : null,
          scheduleMinute: Number.isFinite(Number(raw?.scheduleMinute)) ? Number(raw.scheduleMinute) : 0,
          timezone: String(raw?.timezone ?? ''),
          nextRunAt: raw?.nextRunAt ? String(raw.nextRunAt) : null,
          updatedAt: raw?.updatedAt ? String(raw.updatedAt) : null,
          creditLimit: Number.isFinite(Number(raw?.creditLimit)) ? Number(raw.creditLimit) : null,
          _env: env,
        });
      }
      if (!result.nextKey) break;
      startKey = result.nextKey;
    }
    return all;
  };

  const loadCreditUserSchedulesForEmail = async (targetEmailRaw: string) => {
    if (!adminReady) return;
    const targetEmail = targetEmailRaw.trim().toLowerCase();
    if (!targetEmail) return;
    setCreditUserSchedulesLoadingByEmail((prev) => ({ ...prev, [targetEmail]: true }));
    setCreditUserSchedulesStatus(null);
    try {
      const [prodRes, devRes] = await Promise.allSettled([
        listSchedulesForCreditUser(targetEmail, API_CONFIG.USERS_API, 'prod'),
        listSchedulesForCreditUser(targetEmail, API_CONFIG.USERS_API_DEV, 'dev'),
      ]);
      const merged: CreditUserScheduleItem[] = [];
      const errors: string[] = [];
      if (prodRes.status === 'fulfilled') {
        merged.push(...prodRes.value);
      } else {
        errors.push(`prod: ${prodRes.reason instanceof Error ? prodRes.reason.message : String(prodRes.reason)}`);
      }
      if (devRes.status === 'fulfilled') {
        merged.push(...devRes.value);
      } else {
        errors.push(`dev: ${devRes.reason instanceof Error ? devRes.reason.message : String(devRes.reason)}`);
      }
      merged.sort((a, b) => (Date.parse(String(a.nextRunAt ?? '')) || 0) - (Date.parse(String(b.nextRunAt ?? '')) || 0));
      setCreditUserSchedules((prev) => {
        const keep = prev.filter((item) => String(item.userEmail || '').trim().toLowerCase() !== targetEmail);
        return [...keep, ...merged];
      });
      setCreditUserSchedulesLoadedByEmail((prev) => ({ ...prev, [targetEmail]: true }));
      if (errors.length > 0) {
        setCreditUserSchedulesStatus(
          `Loaded ${merged.length} schedules for ${targetEmail} with warnings (${errors.join(' | ')})`
        );
      } else {
        setCreditUserSchedulesStatus(`Loaded ${merged.length} schedules for ${targetEmail} (prod + dev).`);
      }
    } catch (error: any) {
      setCreditUserSchedulesStatus(error?.message || 'Failed to load schedules.');
    } finally {
      setCreditUserSchedulesLoadingByEmail((prev) => ({ ...prev, [targetEmail]: false }));
    }
  };

  const loadGhlAccounts = async () => {
    if (!adminReady) return;
    setGhlSaving(true);
    setGhlStatus(null);
    try {
      const result = await listGhlAccountsAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load GHL accounts.');
      setGhlAccounts(result.items ?? []);
      setGhlStatus(`Loaded ${(result.items ?? []).length} GHL accounts.`);
    } catch (error: any) {
      setGhlStatus(error?.message || 'Failed to load GHL accounts.');
    } finally {
      setGhlSaving(false);
    }
  };

  const loadInstantlyMetrics = async (withSync = false) => {
    if (!adminReady) return;
    setInstantlyLoading(true);
    setInstantlyStatus(null);
    try {
      if (withSync) {
        const syncResult = await syncInstantlyMetricsAsAdmin({
          requesterEmail: userEmail.trim(),
          token: adminToken,
          userEmail: instantlyFilterUserEmail.trim() || undefined,
          search:
            instantlyFilterCampaignId && instantlyFilterCampaignId !== '__all__'
              ? instantlyCampaignOptions.find((c) => c.id === instantlyFilterCampaignId)?.name || ''
              : '',
          maxCampaigns: 100,
          maxPagesPerCampaign: 10,
          leadsPageSize: 100,
        });
        if (!syncResult.success) throw new Error(syncResult.error || 'Instantly sync failed.');
      }

      const result = await listInstantlyMetricsAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        userEmail: instantlyFilterUserEmail.trim() || undefined,
        campaignId: instantlyFilterCampaignId && instantlyFilterCampaignId !== '__all__' ? instantlyFilterCampaignId : undefined,
        senderEmail: instantlyFilterSenderEmail.trim() || undefined,
        start: instantlyFilterStart ? new Date(instantlyFilterStart).toISOString() : undefined,
        end: instantlyFilterEnd ? new Date(`${instantlyFilterEnd}T23:59:59.999Z`).toISOString() : undefined,
        limit: Number(instantlyLimit) || 20000,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load Instantly analytics.');
      setInstantlyCampaignRows(Array.isArray(result.byCampaign) ? result.byCampaign : []);
      setInstantlySenderRows(Array.isArray(result.bySender) ? result.bySender : []);
      setInstantlyDayRows(Array.isArray(result.byDay) ? result.byDay : []);
      setInstantlyPositiveReplies(Array.isArray(result.positiveReplies) ? result.positiveReplies : []);
      setInstantlyTotals((result.totals as Record<string, number>) ?? {});
      setInstantlyUniqueTotals((result.uniqueTotals as Record<string, number>) ?? {});
      setInstantlyStatus([
        withSync ? 'API sync complete.' : null,
        `Loaded ${(result.sampleSize ?? 0).toLocaleString()} events across ${(result.byCampaign ?? []).length.toLocaleString()} campaign(s).`,
      ].filter(Boolean).join(' '));
    } catch (error: any) {
      setInstantlyStatus(error?.message || 'Instantly analytics load failed.');
    } finally {
      setInstantlyLoading(false);
    }
  };

  const csvEscape = (value: unknown): string => {
    const raw = value === null || value === undefined ? '' : String(value);
    if (/[",\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
    return raw;
  };

  const downloadCsv = (filename: string, headers: string[], rows: Array<Array<unknown>>) => {
    const csv = [
      headers.map(csvEscape).join(','),
      ...rows.map((row) => row.map(csvEscape).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportInstantlyReportCsv = () => {
    const rangeStart = instantlyFilterStart || 'all';
    const rangeEnd = instantlyFilterEnd || 'all';
    const campaignName =
      instantlyFilterCampaignId && instantlyFilterCampaignId !== '__all__'
        ? instantlyCampaignOptions.find((c) => c.id === instantlyFilterCampaignId)?.name || instantlyFilterCampaignId
        : 'all-campaigns';
    const safeCampaign = campaignName.replace(/[^a-z0-9_-]+/gi, '-').slice(0, 60) || 'campaign';
    const base = `instantly-report-${rangeStart}-to-${rangeEnd}-${safeCampaign}`;

    downloadCsv(
      `${base}-summary.csv`,
      [
        'from',
        'to',
        'campaign',
        'sender_filter',
        'sends',
        'opens',
        'clicks',
        'replies',
        'unique_opens',
        'unique_clicks',
        'unique_replies',
      ],
      [[
        instantlyFilterStart || '',
        instantlyFilterEnd || '',
        campaignName,
        instantlyFilterSenderEmail || '',
        Number(instantlyTotals.sends ?? 0),
        Number(instantlyTotals.opens ?? 0),
        Number(instantlyTotals.clicks ?? 0),
        Number(instantlyTotals.replies ?? 0),
        Number(instantlyUniqueTotals.unique_opens ?? 0),
        Number(instantlyUniqueTotals.unique_clicks ?? 0),
        Number(instantlyUniqueTotals.unique_replies ?? 0),
      ]]
    );

    downloadCsv(
      `${base}-campaigns.csv`,
      ['campaign_name', 'campaign_id', 'sends', 'opens', 'clicks', 'replies', 'unique_opens', 'unique_clicks', 'unique_replies', 'bounces', 'unsubscribes', 'total'],
      instantlyCampaignRows.map((row) => [
        row.campaign_name || '',
        row.campaign_id || '',
        Number(row.sends ?? 0),
        Number(row.opens ?? 0),
        Number(row.clicks ?? 0),
        Number(row.replies ?? 0),
        Number(row.unique_opens ?? 0),
        Number(row.unique_clicks ?? 0),
        Number(row.unique_replies ?? 0),
        Number(row.bounces ?? 0),
        Number(row.unsubscribes ?? 0),
        Number(row.total ?? 0),
      ])
    );

    downloadCsv(
      `${base}-daily.csv`,
      ['day', 'sends', 'opens', 'clicks', 'replies', 'unique_opens', 'unique_clicks', 'unique_replies', 'total'],
      instantlyDayRows.map((row) => [
        row.day || '',
        Number(row.sends ?? 0),
        Number(row.opens ?? 0),
        Number(row.clicks ?? 0),
        Number(row.replies ?? 0),
        Number(row.unique_opens ?? 0),
        Number(row.unique_clicks ?? 0),
        Number(row.unique_replies ?? 0),
        Number(row.total ?? 0),
      ])
    );

    downloadCsv(
      `${base}-senders.csv`,
      ['sender_email', 'sends', 'opens', 'clicks', 'replies', 'bounces', 'unsubscribes', 'total'],
      instantlySenderRows.map((row) => [
        row.sender_email || '',
        Number(row.sends ?? 0),
        Number(row.opens ?? 0),
        Number(row.clicks ?? 0),
        Number(row.replies ?? 0),
        Number(row.bounces ?? 0),
        Number(row.unsubscribes ?? 0),
        Number(row.total ?? 0),
      ])
    );

    downloadCsv(
      `${base}-replies.csv`,
      ['campaign_name', 'campaign_id', 'lead_name', 'lead_email', 'sender_email', 'reply_count', 'lt_interest_status', 'last_reply_at'],
      instantlyPositiveReplies.map((row) => [
        row.campaign_name || '',
        row.campaign_id || '',
        row.lead_name || '',
        row.lead_email || '',
        row.sender_email || '',
        Number(row.reply_count ?? 0),
        Number(row.lt_interest_status ?? 0),
        row.last_reply_at || '',
      ])
    );

    setInstantlyStatus('Exported report CSV files (summary, campaigns, daily, senders, replies).');
  };

  const loadLogs = async (kind = logTab) => {
    if (!adminReady) return;
    setLogLoading(true);
    setLogStatus(null);
    try {
      if (kind === 'website_requests') {
        if (!websiteRequestsReady) throw new Error('Website Requests access is limited to @profitibull.com users.');
        const [websiteResult, testDriveResult] = await Promise.all([
          listWebsiteRequests({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            limit: 200,
          }),
          listTestDriveRequests({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            limit: 200,
          }),
        ]);
        if (!websiteResult.success && !testDriveResult.success) {
          throw new Error(
            websiteResult.error ||
              testDriveResult.error ||
              'Failed to load website requests.'
          );
        }
        const items = [
          ...((websiteResult.items ?? []) as WebsiteRequest[]).map((item) => normalizeWebsiteRequest(item, 'website')),
          ...((testDriveResult.items ?? []) as WebsiteRequest[]).map((item) => normalizeWebsiteRequest(item, 'test_drive')),
        ].sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
        setWebsiteRequests(items);
        setWebsiteDrafts(
          items.reduce<Record<string, { emailSentDate: string; pdfUrl: string }>>((acc, item) => {
            const requestId = String(item.requestId ?? '').trim();
            if (!requestId) return acc;
            acc[requestId] = {
              emailSentDate: String(item.emailSentDate ?? ''),
              pdfUrl: String(item.pdfUrl ?? ''),
            };
            return acc;
          }, {})
        );
        setLogStatus(`Loaded ${items.length} website requests.`);
        return;
      }
      if (!isAdmin) throw new Error('Log tabs are admin-only.');
      const limit = Math.min(Number(logLimit) || 100, 500);
      const base = { requesterEmail: userEmail.trim(), token: adminToken, limit };
      const result =
        kind === 'inbound'
          ? await listWebhookInboundLogsAsAdmin(base)
          : kind === 'outbound'
            ? await listWebhookOutboundLogsAsAdmin(base)
            : await listErrorLogsAsAdmin(base);
      if (!result.success) throw new Error(result.error || 'Failed to load logs.');
      if (kind === 'inbound') setWebhookInbound((result.items ?? []) as LogItem[]);
      if (kind === 'outbound') setWebhookOutbound((result.items ?? []) as LogItem[]);
      if (kind === 'errors') setErrorLogs((result.items ?? []) as LogItem[]);
      setLogStatus(`Loaded ${kind} logs.`);
    } catch (error: any) {
      setLogStatus(error?.message || 'Log load failed.');
    } finally {
      setLogLoading(false);
    }
  };

  const loadSchedules = async () => {
    if (!adminReady) return;
    setScheduleLoading(true);
    setScheduleStatus(null);
    try {
      const filterEmail = scheduleSearch.trim() || undefined;
      const base = {
        requesterEmail: userEmail.trim(),
        token: adminToken,
        requesterPassword: scheduleAdminPassword || undefined,
        limit: 200,
        filterEmail,
      };
      if (scheduleEnv === 'all') {
        const [prodRes, devRes] = await Promise.all([
          listSchedulesAsAdmin({ ...base, baseUrl: API_CONFIG.USERS_API }),
          listSchedulesAsAdmin({ ...base, baseUrl: API_CONFIG.USERS_API_DEV }),
        ]);
        if (!prodRes.success && !devRes.success) {
          throw new Error(prodRes.error || devRes.error || 'Failed to load schedules.');
        }
        const prodItems = (prodRes.items ?? []).map((item) => ({ ...item, _env: 'prod' }));
        const devItems = (devRes.items ?? []).map((item) => ({ ...item, _env: 'dev' }));
        const merged = [...prodItems, ...devItems];
        setSchedules(merged);
        setScheduleStatus(`Loaded ${merged.length} schedules.`);
      } else {
        const baseUrl = scheduleEnv === 'dev' ? API_CONFIG.USERS_API_DEV : API_CONFIG.USERS_API;
        const result = await listSchedulesAsAdmin({ ...base, baseUrl });
        if (!result.success) throw new Error(result.error || 'Failed to load schedules.');
        const items = (result.items ?? []).map((item) => ({ ...item, _env: scheduleEnv }));
        setSchedules(items);
        setScheduleStatus(`Loaded ${items.length} schedules.`);
      }
    } catch (error: any) {
      setScheduleStatus(error?.message || 'Schedule load failed.');
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadScheduleRuns = async (tab = scheduleRunTab) => {
    if (!adminReady) return;
    setScheduleRunLoading(true);
    setScheduleRunStatus(null);
    try {
      const filterEmail = scheduleSearch.trim() || undefined;
      const base = {
        requesterEmail: userEmail.trim(),
        token: adminToken,
        requesterPassword: scheduleAdminPassword || undefined,
        limit: 200,
        filterEmail,
        status: tab === 'errors' ? 'ERROR' : undefined,
      };
      if (scheduleEnv === 'all') {
        const [prodRes, devRes] = await Promise.all([
          listScheduleRunsAsAdmin({ ...base, baseUrl: API_CONFIG.USERS_API }),
          listScheduleRunsAsAdmin({ ...base, baseUrl: API_CONFIG.USERS_API_DEV }),
        ]);
        if (!prodRes.success && !devRes.success) {
          throw new Error(prodRes.error || devRes.error || 'Failed to load schedule runs.');
        }
        const prodItems = (prodRes.items ?? []).map((item) => ({ ...item, _env: 'prod' }));
        const devItems = (devRes.items ?? []).map((item) => ({ ...item, _env: 'dev' }));
        const merged = [...prodItems, ...devItems];
        setScheduleRuns(merged);
        setScheduleRunStatus(`Loaded ${merged.length} run logs.`);
      } else {
        const baseUrl = scheduleEnv === 'dev' ? API_CONFIG.USERS_API_DEV : API_CONFIG.USERS_API;
        const result = await listScheduleRunsAsAdmin({ ...base, baseUrl });
        if (!result.success) throw new Error(result.error || 'Failed to load schedule runs.');
        const items = (result.items ?? []).map((item) => ({ ...item, _env: scheduleEnv }));
        setScheduleRuns(items);
        setScheduleRunStatus(`Loaded ${items.length} run logs.`);
      }
    } catch (error: any) {
      setScheduleRunStatus(error?.message || 'Run log load failed.');
    } finally {
      setScheduleRunLoading(false);
    }
  };

  const updateSchedule = async (item: any, updates: Record<string, any>) => {
    if (!adminReady) return;
    const baseUrl = item?._env === 'dev' ? API_CONFIG.USERS_API_DEV : API_CONFIG.USERS_API;
    const result = await updateScheduleAsAdmin({
      requesterEmail: userEmail.trim(),
      requesterPassword: scheduleAdminPassword || undefined,
      token: adminToken,
      baseUrl,
      userEmail: String(item.userEmail ?? '').trim(),
      scheduleId: String(item.scheduleId ?? '').trim(),
      updates,
    });
    if (!result.success) throw new Error(result.error || 'Update failed.');
  };

  const deleteSchedule = async (item: any) => {
    if (!adminReady) return;
    const baseUrl = item?._env === 'dev' ? API_CONFIG.USERS_API_DEV : API_CONFIG.USERS_API;
    const result = await deleteScheduleAsAdmin({
      requesterEmail: userEmail.trim(),
      requesterPassword: scheduleAdminPassword || undefined,
      token: adminToken,
      baseUrl,
      userEmail: String(item.userEmail ?? '').trim(),
      scheduleId: String(item.scheduleId ?? '').trim(),
    });
    if (!result.success) throw new Error(result.error || 'Delete failed.');
  };


  const filteredUserList = useMemo(() => {
    let list = [...userList];
    if (listSearch) {
      const q = listSearch.toLowerCase();
      list = list.filter((u) => u.email.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const dir = listSortDir === 'asc' ? 1 : -1;
      if (listSort === 'email') return a.email.localeCompare(b.email) * dir;
      if (listSort === 'credits') return ((a.credits ?? 0) - (b.credits ?? 0)) * dir;
      if (listSort === 'scheduleCount') return ((a.scheduleCount ?? 0) - (b.scheduleCount ?? 0)) * dir;
      const aTime = a.lastLoginAt ? Date.parse(a.lastLoginAt) : 0;
      const bTime = b.lastLoginAt ? Date.parse(b.lastLoginAt) : 0;
      return (aTime - bTime) * dir;
    });
    return list;
  }, [userList, listSearch, listSort, listSortDir]);

  const missingAssignedCampaignIds = useMemo(() => {
    if (instantlyAssignmentSelectedIds.length === 0) return [];
    const known = new Set(instantlyCampaignOptions.map((campaign) => campaign.id));
    return instantlyAssignmentSelectedIds.filter((id) => !known.has(id));
  }, [instantlyAssignmentSelectedIds, instantlyCampaignOptions]);

  const activeLogs = useMemo(() => {
    if (logTab === 'inbound') return webhookInbound;
    if (logTab === 'outbound') return webhookOutbound;
    if (logTab === 'website_requests') return [];
    return errorLogs;
  }, [logTab, webhookInbound, webhookOutbound, errorLogs]);

  const filteredLogs = useMemo(() => {
    if (!logSearch) return activeLogs;
    const q = logSearch.toLowerCase();
    return activeLogs.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [activeLogs, logSearch]);

  const filteredWebsiteRequests = useMemo(() => {
    if (!logSearch) return websiteRequests;
    const q = logSearch.toLowerCase();
    return websiteRequests.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [websiteRequests, logSearch]);

  const ghlUsers = useMemo(() => {
    const q = ghlSearch.trim().toLowerCase();
    const sorted = [...ghlAccounts].sort((a, b) =>
      String(a.accountName ?? '').localeCompare(String(b.accountName ?? ''), undefined, { sensitivity: 'base' })
    );
    if (!q) return sorted;
    return sorted.filter((item) =>
      [item.accountName, item.email ?? '', item.locationId]
        .map((value) => String(value || '').toLowerCase())
        .some((value) => value.includes(q))
    );
  }, [ghlAccounts, ghlSearch]);

  const instantlyTopCampaigns = useMemo(
    () => [...instantlyCampaignRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [instantlyCampaignRows]
  );

  const instantlyTopSenders = useMemo(
    () => [...instantlySenderRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [instantlySenderRows]
  );

  const instantlyChartRows = useMemo(
    () =>
      [...instantlyDayRows]
        .sort((a, b) => String(a.day ?? '').localeCompare(String(b.day ?? '')))
        .slice(-31),
    [instantlyDayRows]
  );

  const instantlyOpenRate = useMemo(() => {
    const sends = Number(instantlyTotals.sends ?? 0);
    const opens = Number(instantlyTotals.opens ?? 0);
    return sends > 0 ? (opens / sends) * 100 : 0;
  }, [instantlyTotals.opens, instantlyTotals.sends]);

  const instantlyClickRate = useMemo(() => {
    const sends = Number(instantlyTotals.sends ?? 0);
    const clicks = Number(instantlyTotals.clicks ?? 0);
    return sends > 0 ? (clicks / sends) * 100 : 0;
  }, [instantlyTotals.clicks, instantlyTotals.sends]);

  const instantlyReplyRate = useMemo(() => {
    const sends = Number(instantlyTotals.sends ?? 0);
    const replies = Number(instantlyTotals.replies ?? 0);
    return sends > 0 ? (replies / sends) * 100 : 0;
  }, [instantlyTotals.replies, instantlyTotals.sends]);

  const chartValueFmt = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const formatChartDay = (value: string) => {
    if (!value) return '';
    const dt = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const selectedReply = useMemo(
    () => instantlyPositiveReplies.find((row) => row.reply_key === selectedReplyKey) ?? null,
    [instantlyPositiveReplies, selectedReplyKey]
  );

  useEffect(() => {
    if (!selectedReply) return;
    setReplyDraftMessage(String(selectedReply.message_text ?? ''));
    setReplyDraftNote(String(selectedReply.note ?? ''));
  }, [selectedReply?.reply_key]);

  const upsertReplyLocal = (updated: InstantlyPositiveReply) => {
    setInstantlyPositiveReplies((prev) => {
      const idx = prev.findIndex((row) => row.reply_key === updated.reply_key);
      if (idx === -1) return [updated, ...prev];
      const next = [...prev];
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
  };

  const saveReplyEdits = async () => {
    if (!adminReady || !selectedReply) return;
    setReplySaving(true);
    try {
      const result = await upsertInstantlyReplyAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        reply_key: selectedReply.reply_key,
        campaign_id: selectedReply.campaign_id,
        campaign_name: selectedReply.campaign_name ?? null,
        lead_email: selectedReply.lead_email,
        lead_name: selectedReply.lead_name,
        sender_email: selectedReply.sender_email ?? null,
        reply_count: selectedReply.reply_count,
        lt_interest_status: selectedReply.lt_interest_status,
        last_reply_at: selectedReply.last_reply_at ?? null,
        message_text: replyDraftMessage.trim() || null,
        note: replyDraftNote.trim() || null,
        starred: Boolean(selectedReply.starred),
      });
      if (!result.success || !result.row) throw new Error(result.error || 'Failed to save reply.');
      upsertReplyLocal(result.row);
      setInstantlyStatus('Reply details saved.');
    } catch (error: any) {
      setInstantlyStatus(error?.message || 'Failed to save reply details.');
    } finally {
      setReplySaving(false);
    }
  };

  const toggleReplyStar = async (row: InstantlyPositiveReply) => {
    if (!adminReady) return;
    const nextStar = !Boolean(row.starred);
    try {
      const result = await upsertInstantlyReplyAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        reply_key: row.reply_key,
        campaign_id: row.campaign_id,
        campaign_name: row.campaign_name ?? null,
        lead_email: row.lead_email,
        lead_name: row.lead_name,
        sender_email: row.sender_email ?? null,
        reply_count: row.reply_count,
        lt_interest_status: row.lt_interest_status,
        last_reply_at: row.last_reply_at ?? null,
        message_text: row.message_text ?? null,
        note: row.note ?? null,
        starred: nextStar,
      });
      if (!result.success || !result.row) throw new Error(result.error || 'Failed to update star.');
      upsertReplyLocal(result.row);
    } catch (error: any) {
      setInstantlyStatus(error?.message || 'Failed to update star.');
    }
  };

  const deleteReplyRow = async (row: InstantlyPositiveReply) => {
    if (!adminReady) return;
    try {
      const result = await deleteInstantlyReplyAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        reply_key: row.reply_key,
        lead_email: row.lead_email,
        sender_email: row.sender_email ?? null,
      });
      if (!result.success) throw new Error(result.error || 'Failed to delete reply.');
      setInstantlyPositiveReplies((prev) => prev.filter((item) => item.reply_key !== row.reply_key));
      if (selectedReplyKey === row.reply_key) setSelectedReplyKey(null);
      setInstantlyStatus('Reply removed from inbox.');
    } catch (error: any) {
      setInstantlyStatus(error?.message || 'Failed to delete reply.');
    }
  };

  const getLookupStrings = (request: WebsiteRequest) => {
    const lookups = Array.isArray(request.lookups) ? request.lookups : [];
    const titles = Array.from(
      new Set(
        lookups
          .map((lookup) => String(lookup?.title ?? '').trim())
          .filter(Boolean)
      )
    );
    const seniorities = Array.from(
      new Set(
        lookups
          .map((lookup) => String((lookup as any)?.seniority ?? '').trim())
          .filter(Boolean)
      )
    );
    const departments = Array.from(
      new Set(
        lookups
          .map((lookup) => String((lookup as any)?.department ?? '').trim())
          .filter(Boolean)
      )
    );
    const industries = Array.from(
      new Set(
        lookups
          .map((lookup) => String((lookup as any)?.industry ?? '').trim())
          .filter(Boolean)
      )
    );
    const employeeSizes = Array.from(
      new Set(
        lookups
          .map((lookup) => String((lookup as any)?.employeeSize ?? '').trim())
          .filter(Boolean)
      )
    );
    const companies = Array.from(
      new Set(
        lookups
          .map((lookup) => String(lookup?.companyName ?? '').trim())
          .filter(Boolean)
      )
    );
    const titleLike = [...titles];
    if (seniorities.length) titleLike.push(`Seniority: ${seniorities.join(', ')}`);
    if (departments.length) titleLike.push(`Department: ${departments.join(', ')}`);
    if (industries.length) titleLike.push(`Industry: ${industries.join(', ')}`);
    if (employeeSizes.length) titleLike.push(`Employee Size: ${employeeSizes.join(', ')}`);
    return {
      titles: titleLike.join(' | '),
      companies: companies.join(', '),
    };
  };

  const saveWebsiteRequest = async (item: WebsiteRequest) => {
    if (!websiteRequestsReady) return;
    const requestId = String(item.requestId ?? '').trim();
    if (!requestId) return;
    const draft = websiteDrafts[requestId] ?? { emailSentDate: '', pdfUrl: '' };
    setLogLoading(true);
    setLogStatus(null);
    try {
      const isTestDrive = item.sourceType === 'test_drive' || requestId.startsWith('tdr_');
      const result = isTestDrive
        ? await updateTestDriveRequest({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            requestId,
            reportSentDate: draft.emailSentDate.trim() || null,
            reportPdfUrl: draft.pdfUrl.trim() || null,
          })
        : await updateWebsiteRequest({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            requestId,
            emailSentDate: draft.emailSentDate.trim() || null,
            pdfUrl: draft.pdfUrl.trim() || null,
          });
      if (!result.success) throw new Error(result.error || 'Failed to save website request.');
      setWebsiteRequests((prev) =>
        prev.map((row) =>
          String(row.requestId ?? '') === requestId
            ? { ...row, emailSentDate: draft.emailSentDate.trim(), pdfUrl: draft.pdfUrl.trim() }
            : row
        )
      );
      setLogStatus(`Saved website request ${requestId}.`);
    } catch (error: any) {
      setLogStatus(error?.message || 'Website request save failed.');
    } finally {
      setLogLoading(false);
    }
  };

  const sortedSchedules = useMemo(() => {
    const list = [...schedules];
    const dir = scheduleSortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (scheduleSort === 'userEmail') {
        return String(a.userEmail ?? '').localeCompare(String(b.userEmail ?? '')) * dir;
      }
      const aTime = Date.parse(a?.[scheduleSort] ?? '') || 0;
      const bTime = Date.parse(b?.[scheduleSort] ?? '') || 0;
      return (aTime - bTime) * dir;
    });
    return list;
  }, [schedules, scheduleSort, scheduleSortDir]);

  const sortedScheduleRuns = useMemo(() => {
    const list = [...scheduleRuns];
    list.sort((a, b) => {
      const aTime = Date.parse(a?.startedAt ?? a?.finishedAt ?? '') || 0;
      const bTime = Date.parse(b?.startedAt ?? b?.finishedAt ?? '') || 0;
      return bTime - aTime;
    });
    return list;
  }, [scheduleRuns]);

  const scheduleMetricsBySchedule = useMemo(() => {
    const byParent = new Map<string, { scheduleId: string; updatedAt: number; pages: any[] }>();
    const parseTime = (v: any) => {
      const t = Date.parse(String(v ?? ''));
      return Number.isFinite(t) ? t : 0;
    };
    const getScheduleId = (r: any) => String(r?.scheduleId ?? r?.schedule_id ?? '').trim();
    const getItemType = (r: any) => String(r?.itemType ?? r?.item_type ?? '').trim();
    const getSourceRunId = (r: any) => String(r?.sourceRunId ?? r?.source_run_id ?? '').trim();
    const getRunId = (r: any) => String(r?.runId ?? r?.run_id ?? '').trim();

    for (const row of scheduleRuns) {
      const itemType = getItemType(row);
      if (itemType !== 'scheduled_search_page') continue;
      const scheduleId = getScheduleId(row);
      const sourceRunId = getSourceRunId(row) || getRunId(row).split(':page:')[0];
      if (!scheduleId || !sourceRunId) continue;
      const updatedAt = Math.max(parseTime(row?.updatedAt ?? row?.updated_at), parseTime(row?.createdAt ?? row?.created_at));
      const entry = byParent.get(sourceRunId) ?? { scheduleId, updatedAt: 0, pages: [] };
      entry.scheduleId = scheduleId || entry.scheduleId;
      entry.updatedAt = Math.max(entry.updatedAt, updatedAt);
      entry.pages.push(row);
      byParent.set(sourceRunId, entry);
    }

    const latestBySchedule = new Map<string, { updatedAt: number; pages: any[] }>();
    for (const entry of byParent.values()) {
      const existing = latestBySchedule.get(entry.scheduleId);
      if (!existing || entry.updatedAt > existing.updatedAt) {
        latestBySchedule.set(entry.scheduleId, { updatedAt: entry.updatedAt, pages: entry.pages });
      }
    }

    const out = new Map<string, { lastPage: number | null; totalPages: number | null; creditsUsed: number | null }>();
    for (const [scheduleId, run] of latestBySchedule.entries()) {
      const pages = run.pages
        .map((p) => Number(p?.page))
        .filter((n) => Number.isFinite(n));
      const lastPage = pages.length ? Math.max(...pages) : null;
      const totalPages = run.pages.length || null;
      const creditsUsed = run.pages.reduce((sum, p) => sum + Number(p?.researchTriggered ?? p?.research_triggered ?? 0), 0);
      out.set(scheduleId, { lastPage, totalPages, creditsUsed });
    }
    return out;
  }, [scheduleRuns]);

  const handleLookup = async () => {
    if (!adminReady || !targetEmail) {
      setStatus({ type: 'error', message: 'Enter admin credentials and a target email.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await getUserAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: targetEmail.trim(),
      });
      if (!result.success || !result.user) {
        throw new Error(result.error || 'Unable to load user.');
      }
      setUser(result.user);
      setGhlAssignmentSelectedIds(normalizeLocationIds(result.user.ghlAssignedLocationIds));
      setGhlAssignmentDefaultLocationId(String(result.user.ghlSendAsLocationId ?? '').trim());
      setGhlAssignmentStatus(null);
      if (ghlAccounts.length === 0) void loadGhlAccounts();
      setInstantlyAssignmentSelectedIds(normalizeCampaignIds(result.user.instantlyCampaignIds));
      setInstantlyAssignmentStatus(null);
      void loadInstantlyCampaignOptionsForAssignments();
      const features = result.user.features ?? {};
      setFeatureCustomKpi(features.customKpi ?? true);
      setFeatureContentScraping(features.contentScraping ?? true);
      setFeatureProfitPod(features.profitPod ?? true);
      setFeatureBusinessIntel(features.businessIntel ?? true);
      setFeaturePepaiResearch(features.pepaiResearch ?? false);
      setFeatureEmailWorkspace(features.emailWorkspace ?? false);
      setFeatureStatus(null);
      setUserTypeChoice(result.user.userType ?? (result.user.isAdmin ? 'admin' : 'user'));
      setUserTypeStatus(null);
      setWebhookInput(result.user.webhook ?? '');
      setDailyCreditLimitInput(
        result.user.dailyCreditsLimit == null ? '' : String(result.user.dailyCreditsLimit)
      );
      setDailyContactCreditLimitInput(
        result.user.dailyContactCreditsLimit == null ? '' : String(result.user.dailyContactCreditsLimit)
      );
      setGhlPitInput(result.user.ghlPit ?? '');
      setGhlLocationIdInput(result.user.ghlLocationId ?? '');
      setLinkedinCookieInput(result.user.linkedinConnectionCookie ?? '');
      setInstagramCookieInput(result.user.instagramConnectionCookie ?? '');
      setLinkedinCommentAgentInput(result.user.phantomBusterLinkedinCommentAgentId ?? '');
      setInstagramCommentAgentInput(result.user.phantomBusterInstagramCommentAgentId ?? '');
      setPbConnectorIdLiInput(result.user.pbConnectorIdLi ?? '');
      setPbConnectorIdIgInput(result.user.pbConnectorIdIg ?? '');
      setActivity([]);
      setStatus({ type: 'success', message: 'User loaded.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Lookup failed.' });
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!user) return;
    await handleLookup();
  };

  const mutateCreditsAcrossEnvs = async (input: {
    mode: 'add' | 'set';
    targetEmail: string;
    amount: number;
    creditType?: 'scrape' | 'contact';
  }) => {
    const envs = Array.from(new Set([API_CONFIG.USERS_API, API_CONFIG.USERS_API_DEV].filter(Boolean)));
    const results = await Promise.all(
      envs.map(async (baseUrl) => {
        const result =
          input.mode === 'add'
            ? await addCreditsAsAdmin({
                requesterEmail: userEmail.trim(),
                token: adminToken,
                baseUrl,
                targetEmail: input.targetEmail,
                amount: input.amount,
                creditType: input.creditType,
              })
            : await setCreditsAsAdmin({
                requesterEmail: userEmail.trim(),
                token: adminToken,
                baseUrl,
                targetEmail: input.targetEmail,
                amount: input.amount,
                creditType: input.creditType,
              });
        return { baseUrl, result };
      })
    );
    const failed = results.filter(({ result }) => !result.success);
    if (failed.length > 0) {
      const detail = failed
        .map(({ baseUrl, result }) => `${baseUrl}: ${result.error || 'Credit update failed'}`)
        .join(' | ');
      throw new Error(detail);
    }
    const preferred = results.find(({ baseUrl }) => baseUrl === API_CONFIG.USERS_API) ?? results[0];
    return {
      credits: preferred?.result.credits,
      contactCredits: preferred?.result.contactCredits,
    };
  };

  const setDailyLimitsAcrossEnvs = async (input: {
    targetEmail: string;
    dailyCreditsLimit: number | null;
    dailyContactCreditsLimit: number | null;
  }) => {
    const envs = Array.from(new Set([API_CONFIG.USERS_API, API_CONFIG.USERS_API_DEV].filter(Boolean)));
    const results = await Promise.all(
      envs.map(async (baseUrl) => {
        const result = await setDailyLimitsAsAdmin({
          requesterEmail: userEmail.trim(),
          token: adminToken,
          baseUrl,
          targetEmail: input.targetEmail,
          dailyCreditsLimit: input.dailyCreditsLimit,
          dailyContactCreditsLimit: input.dailyContactCreditsLimit,
        });
        return { baseUrl, result };
      })
    );
    const failed = results.filter(({ result }) => !result.success);
    if (failed.length > 0) {
      const detail = failed
        .map(({ baseUrl, result }) => `${baseUrl}: ${result.error || 'Daily limit update failed'}`)
        .join(' | ');
      throw new Error(detail);
    }
    const preferred = results.find(({ baseUrl }) => baseUrl === API_CONFIG.USERS_API) ?? results[0];
    return {
      dailyCreditsLimit: preferred?.result.dailyCreditsLimit ?? null,
      dailyContactCreditsLimit: preferred?.result.dailyContactCreditsLimit ?? null,
    };
  };

  const handleAddCredits = async () => {
    if (!adminReady || !user) return;
    const amount = Number(creditAdd);
    if (!Number.isFinite(amount) || amount <= 0) {
      setStatus({ type: 'error', message: 'Enter a valid credit amount.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await mutateCreditsAcrossEnvs({
        mode: 'add',
        targetEmail: user.email,
        amount,
      });
      setUser({ ...user, credits: result?.credits ?? user.credits + amount });
      setCreditAdd('');
      setStatus({ type: 'success', message: 'Credits added (prod + dev mirror).' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Add credits failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetCredits = async () => {
    if (!adminReady || !user) return;
    const amount = Number(creditSet);
    if (!Number.isFinite(amount) || amount < 0) {
      setStatus({ type: 'error', message: 'Enter a valid credit value.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await mutateCreditsAcrossEnvs({
        mode: 'set',
        targetEmail: user.email,
        amount,
      });
      setUser({ ...user, credits: result?.credits ?? amount });
      setCreditSet('');
      setStatus({ type: 'success', message: 'Credits updated (prod + dev mirror).' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Set credits failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddContactCredits = async () => {
    if (!adminReady || !user) return;
    const amount = Number(contactCreditAdd);
    if (!Number.isFinite(amount) || amount <= 0) {
      setStatus({ type: 'error', message: 'Enter a valid contact credit amount.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await mutateCreditsAcrossEnvs({
        mode: 'add',
        targetEmail: user.email,
        amount,
        creditType: 'contact',
      });
      setUser({ ...user, contactCredits: result?.contactCredits ?? (user.contactCredits ?? 0) + amount });
      setContactCreditAdd('');
      setStatus({ type: 'success', message: 'Contact credits added (prod + dev mirror).' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Add contact credits failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetContactCredits = async () => {
    if (!adminReady || !user) return;
    const amount = Number(contactCreditSet);
    if (!Number.isFinite(amount) || amount < 0) {
      setStatus({ type: 'error', message: 'Enter a valid contact credit value.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await mutateCreditsAcrossEnvs({
        mode: 'set',
        targetEmail: user.email,
        amount,
        creditType: 'contact',
      });
      setUser({ ...user, contactCredits: result?.contactCredits ?? amount });
      setContactCreditSet('');
      setStatus({ type: 'success', message: 'Contact credits updated (prod + dev mirror).' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Set contact credits failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetDailyLimits = async () => {
    if (!adminReady || !user) return;
    const nextDailyCredits =
      dailyCreditLimitInput.trim() === '' ? null : Number(dailyCreditLimitInput);
    const nextDailyContactCredits =
      dailyContactCreditLimitInput.trim() === '' ? null : Number(dailyContactCreditLimitInput);
    if (
      (nextDailyCredits != null && (!Number.isFinite(nextDailyCredits) || nextDailyCredits < 0)) ||
      (nextDailyContactCredits != null && (!Number.isFinite(nextDailyContactCredits) || nextDailyContactCredits < 0))
    ) {
      setStatus({ type: 'error', message: 'Daily limits must be empty or a non-negative number.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await setDailyLimitsAcrossEnvs({
        targetEmail: user.email,
        dailyCreditsLimit: nextDailyCredits == null ? null : Math.floor(nextDailyCredits),
        dailyContactCreditsLimit: nextDailyContactCredits == null ? null : Math.floor(nextDailyContactCredits),
      });
      setUser({
        ...user,
        dailyCreditsLimit: result.dailyCreditsLimit ?? null,
        dailyContactCreditsLimit: result.dailyContactCreditsLimit ?? null,
      });
      setDailyCreditLimitInput(result.dailyCreditsLimit == null ? '' : String(result.dailyCreditsLimit));
      setDailyContactCreditLimitInput(
        result.dailyContactCreditsLimit == null ? '' : String(result.dailyContactCreditsLimit)
      );
      setStatus({ type: 'success', message: 'Daily limits updated (prod + dev mirror).' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Failed to update daily limits.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!adminReady || !user) return;
    if (newPassword.length < 6) {
      setStatus({ type: 'error', message: 'Password must be at least 6 characters.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await setPasswordAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        newPassword,
      });
      if (!result.success) throw new Error(result.error || 'Failed to reset password.');
      setNewPassword('');
      setStatus({ type: 'success', message: 'Password reset.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Reset password failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisabled = async () => {
    if (!adminReady || !user) return;
    setLoading(true);
    setStatus(null);
    try {
      const result = await setDisabledAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        disabled: !user.disabled,
      });
      if (!result.success) throw new Error(result.error || 'Failed to update status.');
      setUser({ ...user, disabled: !user.disabled });
      setStatus({ type: 'success', message: user.disabled ? 'User enabled.' : 'User disabled.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Status update failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetWebhook = async () => {
    if (!adminReady || !user) return;
    setLoading(true);
    setStatus(null);
    try {
      const result = await setWebhookAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        webhook: webhookInput.trim() ? webhookInput.trim() : null,
      });
      if (!result.success) throw new Error(result.error || 'Failed to update webhook.');
      setUser({ ...user, webhook: (result.webhook ?? webhookInput.trim()) || null });
      setStatus({ type: 'success', message: 'Webhook updated.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Webhook update failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetGhlConfig = async () => {
    if (!adminReady || !user) return;
    setLoading(true);
    setStatus(null);
    try {
      const pit = ghlPitInput.trim() ? ghlPitInput.trim() : null;
      const locationId = ghlLocationIdInput.trim() ? ghlLocationIdInput.trim() : null;
      const result = await setGhlConfigAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        pit,
        locationId,
        linkedinConnectionCookie: linkedinCookieInput.trim() ? linkedinCookieInput.trim() : null,
        instagramConnectionCookie: instagramCookieInput.trim() ? instagramCookieInput.trim() : null,
        phantomBusterLinkedinCommentAgentId: linkedinCommentAgentInput.trim() ? linkedinCommentAgentInput.trim() : null,
        phantomBusterInstagramCommentAgentId: instagramCommentAgentInput.trim() ? instagramCommentAgentInput.trim() : null,
        pbConnectorIdLi: pbConnectorIdLiInput.trim() ? pbConnectorIdLiInput.trim() : null,
        pbConnectorIdIg: pbConnectorIdIgInput.trim() ? pbConnectorIdIgInput.trim() : null,
      });
      if (!result.success) throw new Error(result.error || 'Failed to update GHL config.');
      setUser({
        ...user,
        ghlPit: result.ghlPit ?? pit,
        ghlLocationId: result.ghlLocationId ?? locationId,
        linkedinConnectionCookie: result.linkedinConnectionCookie ?? (linkedinCookieInput.trim() || null),
        instagramConnectionCookie: result.instagramConnectionCookie ?? (instagramCookieInput.trim() || null),
        phantomBusterLinkedinCommentAgentId:
          result.phantomBusterLinkedinCommentAgentId ?? (linkedinCommentAgentInput.trim() || null),
        phantomBusterInstagramCommentAgentId:
          result.phantomBusterInstagramCommentAgentId ?? (instagramCommentAgentInput.trim() || null),
        pbConnectorIdLi: result.pbConnectorIdLi ?? (pbConnectorIdLiInput.trim() || null),
        pbConnectorIdIg: result.pbConnectorIdIg ?? (pbConnectorIdIgInput.trim() || null),
      });
      setStatus({ type: 'success', message: 'CRM + connector config updated.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'GHL update failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditGhlAccount = (item: GhlAccountRecord) => {
    setGhlAccountLocationIdInput(String(item.locationId || ''));
    setGhlAccountPitInput(String(item.pit || ''));
    setGhlAccountNameInput(String(item.accountName || ''));
    setGhlAccountEmailInput(String(item.email || ''));
    setTestResult(null);
  };

  const handleSaveGhlAccount = async () => {
    if (!adminReady) return;
    const locationId = ghlAccountLocationIdInput.trim();
    const pit = ghlAccountPitInput.trim();
    const accountName = ghlAccountNameInput.trim();
    const email = ghlAccountEmailInput.trim() || null;
    if (!locationId || !pit || !accountName) {
      setGhlStatus('Location ID, PIT, and Account Name are required.');
      return;
    }
    setGhlSaving(true);
    setGhlStatus(null);
    try {
      const result = await upsertGhlAccountAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        locationId,
        pit,
        accountName,
        email,
        active: true,
      });
      if (!result.success || !result.item) throw new Error(result.error || 'Failed to save GHL account.');
      setGhlStatus(`Saved ${result.item.accountName}.`);
      setGhlAccountLocationIdInput(result.item.locationId);
      setGhlAccountPitInput(result.item.pit);
      setGhlAccountNameInput(result.item.accountName);
      setGhlAccountEmailInput(result.item.email || '');
      await loadGhlAccounts();
    } catch (error: any) {
      setGhlStatus(error?.message || 'Failed to save GHL account.');
    } finally {
      setGhlSaving(false);
    }
  };

  const handleRunGhlTest = async (account?: GhlAccountRecord) => {
    if (!adminReady) return;
    const locationId = String(account?.locationId || ghlAccountLocationIdInput || '').trim();
    const pit = String(account?.pit || ghlAccountPitInput || '').trim();
    if (!locationId || !pit) {
      setTestResult('Location ID and PIT are required to run a test.');
      return;
    }
    setGhlTesting(true);
    setTestResult(null);
    try {
      const result = await testGhlAccountAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        locationId,
        pit,
        contactName: testContactName.trim(),
        email: testContactEmail.trim(),
        companyName: testCompanyName.trim(),
      });
      setTestResult(
        JSON.stringify(
          {
            success: result.success,
            statusCode: result.statusCode,
            requestPayload: result.requestPayload,
            response: result.response,
          },
          null,
          2
        )
      );
    } catch (error: any) {
      setTestResult(error?.message || 'Connection test failed.');
    } finally {
      setGhlTesting(false);
    }
  };

  const handleProvisionGhlFields = async (account?: GhlAccountRecord) => {
    if (!adminReady) return;
    const locationId = String(account?.locationId || ghlAccountLocationIdInput || '').trim();
    const pit = String(account?.pit || ghlAccountPitInput || '').trim();
    const fieldNames = Array.from(
      new Set(
        ghlFieldNamesInput
          .split(/\r?\n|,/)
          .map((name) => name.trim())
          .filter(Boolean)
      )
    );
    if (!locationId) {
      setGhlFieldProvisionStatus(null);
      setGhlFieldProvisionResult('Location ID is required.');
      return;
    }
    if (fieldNames.length === 0) {
      setGhlFieldProvisionStatus(null);
      setGhlFieldProvisionResult('At least one custom field name is required.');
      return;
    }
    setGhlFieldProvisioning(true);
    setGhlFieldProvisionStatus('Starting custom field provisioning...');
    setGhlFieldProvisionResult(null);
    try {
      const batchSize = 25;
      const total = fieldNames.length;
      const totalBatches = Math.max(1, Math.ceil(total / batchSize));
      const aggregate: {
        success: boolean;
        locationId: string;
        totalRequested: number;
        processed: number;
        batchesCompleted: number;
        created: Array<{ name: string; fieldKey?: string; id?: string }>;
        skipped: Array<{ name: string; fieldKey?: string; id?: string }>;
        failed: Array<{ name: string; error: string }>;
        lastBatch?: unknown;
      } = {
        success: true,
        locationId,
        totalRequested: total,
        processed: 0,
        batchesCompleted: 0,
        created: [],
        skipped: [],
        failed: [],
      };

      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
      const runBatch = async (chunk: string[]) => {
        let lastError: any = null;
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          try {
            return await provisionGhlCustomFieldsAsAdmin({
              requesterEmail: userEmail.trim(),
              token: adminToken,
              locationId,
              pit: pit || undefined,
              fieldNames: chunk,
            });
          } catch (error: any) {
            lastError = error;
            const message = String(error?.message || error || '');
            const transient = /503|timeout|network|fetch/i.test(message);
            if (!transient || attempt === 3) break;
            await sleep(400 * attempt);
          }
        }
        throw lastError;
      };

      for (let index = 0; index < total; index += batchSize) {
        const batchNo = Math.floor(index / batchSize) + 1;
        const chunk = fieldNames.slice(index, index + batchSize);
        setGhlFieldProvisionStatus(
          `Processing batch ${batchNo}/${totalBatches} (${aggregate.processed}/${total} processed)...`
        );

        const batchResult = await runBatch(chunk);
        const created = Array.isArray(batchResult.created) ? batchResult.created : [];
        const skipped = Array.isArray(batchResult.skipped) ? batchResult.skipped : [];
        const failed = Array.isArray(batchResult.failed) ? batchResult.failed : [];
        aggregate.created.push(...created);
        aggregate.skipped.push(...skipped);
        aggregate.failed.push(...failed);
        aggregate.processed += chunk.length;
        aggregate.batchesCompleted = batchNo;
        aggregate.lastBatch = batchResult;
        aggregate.success = aggregate.failed.length === 0;

        setGhlFieldProvisionResult(
          JSON.stringify(
            {
              success: aggregate.success,
              locationId: aggregate.locationId,
              totalRequested: aggregate.totalRequested,
              processed: aggregate.processed,
              batchesCompleted: aggregate.batchesCompleted,
              totalBatches,
              createdCount: aggregate.created.length,
              skippedCount: aggregate.skipped.length,
              failedCount: aggregate.failed.length,
              lastBatch: aggregate.lastBatch,
              failedPreview: aggregate.failed.slice(0, 25),
            },
            null,
            2
          )
        );
      }

      setGhlFieldProvisionStatus(
        aggregate.success
          ? `Completed. Created ${aggregate.created.length}, skipped ${aggregate.skipped.length}, failed ${aggregate.failed.length}.`
          : `Completed with errors. Created ${aggregate.created.length}, skipped ${aggregate.skipped.length}, failed ${aggregate.failed.length}.`
      );
      setGhlFieldProvisionResult(
        JSON.stringify(
          {
            success: aggregate.success,
            locationId: aggregate.locationId,
            totalRequested: aggregate.totalRequested,
            processed: aggregate.processed,
            batchesCompleted: aggregate.batchesCompleted,
            totalBatches,
            createdCount: aggregate.created.length,
            skippedCount: aggregate.skipped.length,
            failedCount: aggregate.failed.length,
            created: aggregate.created,
            skipped: aggregate.skipped,
            failed: aggregate.failed,
          },
          null,
          2
        )
      );
    } catch (error: any) {
      setGhlFieldProvisionStatus('Provisioning stopped due to API error.');
      setGhlFieldProvisionResult(error?.message || 'Custom field provisioning failed.');
    } finally {
      setGhlFieldProvisioning(false);
    }
  };

  const handleSetFeatures = async () => {
    if (!adminReady || !user) return;
    setLoading(true);
    setFeatureStatus(null);
    try {
      const featuresPayload = {
        customKpi: featureCustomKpi,
        contentScraping: featureContentScraping,
        profitPod: featureProfitPod,
        businessIntel: featureBusinessIntel,
        pepaiResearch: featurePepaiResearch,
        emailWorkspace: featureEmailWorkspace,
      };
      const envs = Array.from(new Set([API_CONFIG.USERS_API, API_CONFIG.USERS_API_DEV].filter(Boolean)));
      const results = await Promise.all(
        envs.map(async (baseUrl) => {
          const result = await setFeaturesAsAdmin({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            baseUrl,
            targetEmail: user.email,
            features: featuresPayload,
          });
          return { baseUrl, result };
        })
      );
      const failed = results.filter(({ result }) => !result.success);
      if (failed.length > 0) {
        const detail = failed
          .map(({ baseUrl, result }) => `${baseUrl}: ${result.error || 'Feature update failed'}`)
          .join(' | ');
        throw new Error(detail);
      }
      const preferred = results.find(({ baseUrl }) => baseUrl === API_CONFIG.USERS_API) ?? results[0];
      setFeatureStatus('Access settings saved.');
      setUser({ ...user, features: preferred?.result.features ?? user.features });
    } catch (error: any) {
      setFeatureStatus(error?.message || 'Failed to update features.');
    } finally {
      setLoading(false);
    }
  };

  const toggleInstantlyCampaignAssignment = (campaignId: string) => {
    setInstantlyAssignmentSelectedIds((prev) =>
      prev.includes(campaignId) ? prev.filter((id) => id !== campaignId) : [...prev, campaignId]
    );
  };

  const handleSaveInstantlyCampaignAssignments = async () => {
    if (!adminReady || !user) return;
    setInstantlyAssignmentLoading(true);
    setInstantlyAssignmentStatus(null);
    try {
      const envs = Array.from(new Set([API_CONFIG.USERS_API, API_CONFIG.USERS_API_DEV].filter(Boolean)));
      const results = await Promise.all(
        envs.map(async (baseUrl) => {
          const result = await setInstantlyCampaignAssignmentsAsAdmin({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            baseUrl,
            targetEmail: user.email,
            instantlyCampaignIds: instantlyAssignmentSelectedIds,
          });
          return { baseUrl, result };
        })
      );
      const failed = results.filter(({ result }) => !result.success);
      if (failed.length > 0) {
        const detail = failed
          .map(({ baseUrl, result }) => `${baseUrl}: ${result.error || 'Campaign assignment update failed'}`)
          .join(' | ');
        throw new Error(detail);
      }
      const preferred = results.find(({ baseUrl }) => baseUrl === API_CONFIG.USERS_API) ?? results[0];
      const saved = normalizeCampaignIds(preferred?.result.instantlyCampaignIds ?? instantlyAssignmentSelectedIds);
      setInstantlyAssignmentSelectedIds(saved);
      setUser({ ...user, instantlyCampaignIds: saved });
      setInstantlyAssignmentStatus(
        `Saved ${saved.length} campaign assignment${saved.length === 1 ? '' : 's'} (prod + dev mirror).`
      );
    } catch (error: any) {
      setInstantlyAssignmentStatus(error?.message || 'Failed to save campaign assignments.');
    } finally {
      setInstantlyAssignmentLoading(false);
    }
  };

  const toggleGhlLocationAssignment = (locationId: string) => {
    setGhlAssignmentSelectedIds((prev) => {
      const exists = prev.includes(locationId);
      const next = exists ? prev.filter((id) => id !== locationId) : [...prev, locationId];
      if (!next.includes(ghlAssignmentDefaultLocationId)) {
        setGhlAssignmentDefaultLocationId(next.length === 1 ? next[0] : '');
      }
      return next;
    });
  };

  const handleSaveGhlLocationAssignments = async () => {
    if (!adminReady || !user) return;
    setGhlAssignmentSaving(true);
    setGhlAssignmentStatus(null);
    try {
      const nextAssigned = normalizeLocationIds(ghlAssignmentSelectedIds);
      const nextDefault =
        ghlAssignmentDefaultLocationId && nextAssigned.includes(ghlAssignmentDefaultLocationId)
          ? ghlAssignmentDefaultLocationId
          : nextAssigned.length === 1
            ? nextAssigned[0]
            : null;
      const envs = Array.from(new Set([API_CONFIG.USERS_API, API_CONFIG.USERS_API_DEV].filter(Boolean)));
      const results = await Promise.all(
        envs.map(async (baseUrl) => {
          const result = await setGhlLocationAssignmentsAsAdmin({
            requesterEmail: userEmail.trim(),
            token: adminToken,
            baseUrl,
            targetEmail: user.email,
            ghlAssignedLocationIds: nextAssigned,
            ghlSendAsLocationId: nextDefault,
          });
          return { baseUrl, result };
        })
      );
      const failed = results.filter(({ result }) => !result.success);
      if (failed.length > 0) {
        const detail = failed
          .map(({ baseUrl, result }) => `${baseUrl}: ${result.error || 'GHL assignment update failed'}`)
          .join(' | ');
        throw new Error(detail);
      }
      const preferred = results.find(({ baseUrl }) => baseUrl === API_CONFIG.USERS_API) ?? results[0];
      const savedAssigned = normalizeLocationIds(preferred?.result.ghlAssignedLocationIds ?? nextAssigned);
      const savedDefault = String(preferred?.result.ghlSendAsLocationId ?? '').trim();
      setGhlAssignmentSelectedIds(savedAssigned);
      setGhlAssignmentDefaultLocationId(savedDefault);
      setUser({
        ...user,
        ghlAssignedLocationIds: savedAssigned,
        ghlSendAsLocationId: savedDefault || null,
      });
      setGhlAssignmentStatus(
        `Saved ${savedAssigned.length} assigned GHL location${savedAssigned.length === 1 ? '' : 's'} (prod + dev mirror).`
      );
    } catch (error: any) {
      setGhlAssignmentStatus(error?.message || 'Failed to save GHL assignments.');
    } finally {
      setGhlAssignmentSaving(false);
    }
  };

  const handleSetUserType = async () => {
    if (!adminReady || !user) return;
    setLoading(true);
    setUserTypeStatus(null);
    try {
      const result = await setUserTypeAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        userType: userTypeChoice,
      });
      if (!result.success) throw new Error(result.error || 'Failed to update user type.');
      setUserTypeStatus('User type updated.');
      setUser({
        ...user,
        userType: result.userType ?? userTypeChoice,
        features: result.features ?? user.features,
      });
      if (result.features) {
        setFeatureCustomKpi(result.features.customKpi ?? false);
        setFeatureContentScraping(result.features.contentScraping ?? false);
        setFeatureProfitPod(result.features.profitPod ?? false);
        setFeatureBusinessIntel(result.features.businessIntel ?? false);
        setFeaturePepaiResearch(result.features.pepaiResearch ?? false);
        setFeatureEmailWorkspace(result.features.emailWorkspace ?? false);
      }
    } catch (error: any) {
      setUserTypeStatus(error?.message || 'Failed to update user type.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadActivity = async () => {
    if (!adminReady || !user) return;
    const limit = Number(activityLimit);
    if (!Number.isFinite(limit) || limit <= 0) {
      setStatus({ type: 'error', message: 'Enter a valid activity limit.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await getActivityAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: user.email,
        limit,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load activity.');
      setActivity((result.items ?? []) as ActivityItem[]);
      setStatus({ type: 'success', message: 'Activity loaded.' });
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Activity failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (email: string) => {
    setTargetEmail(email);
    await handleLookup();
  };

  const handleCreateUser = async () => {
    if (!adminReady || !createEmail) {
      setStatus({ type: 'error', message: 'Enter admin credentials and a user email.' });
      return;
    }
    const creditsValue = createCredits === '' ? undefined : Number(createCredits);
    if (creditsValue != null && (!Number.isFinite(creditsValue) || creditsValue < 0)) {
      setStatus({ type: 'error', message: 'Invalid credits value.' });
      return;
    }
    if (createPassword && createPassword.length < 6) {
      setStatus({ type: 'error', message: 'Password must be at least 6 characters.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const effectiveUserType = createIsAdmin ? 'admin' : createUserType;
      const result = await createUserAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        targetEmail: createEmail.trim(),
        password: createPassword ? createPassword : undefined,
        credits: creditsValue,
        timezone: createTimezone.trim() ? createTimezone.trim() : undefined,
        isAdmin: createIsAdmin,
        userType: effectiveUserType,
        disabled: createDisabled,
      });
      if (!result.success || !result.user) throw new Error(result.error || 'Failed to create user.');
      setStatus({ type: 'success', message: 'User created.' });
      setTargetEmail(result.user.email);
      setUser(result.user);
      setInstantlyAssignmentSelectedIds(normalizeCampaignIds(result.user.instantlyCampaignIds));
      setInstantlyAssignmentStatus(null);
      setActivity([]);
      setCreateEmail('');
      setCreatePassword('');
      setCreateIsAdmin(false);
      setCreateDisabled(false);
      setCreateUserType('user');
    } catch (error: any) {
      setStatus({ type: 'error', message: error?.message || 'Create user failed.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Command Center</h2>
            <p className="text-gray-400 text-sm">Admin-only access to users, credits, and activity.</p>
          </div>
          <div className="text-xs text-gray-500">Access enforced by admin flag</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSectionTab('ghl')}
            className={`px-3 py-1 rounded text-xs font-semibold ${sectionTab === 'ghl' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
          >
            GHL Accounts
          </button>
          <button
            onClick={() => setSectionTab('instantly')}
            className={`px-3 py-1 rounded text-xs font-semibold ${sectionTab === 'instantly' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
          >
            Instantly Data
          </button>
          <button
            onClick={() => setSectionTab('all')}
            className={`px-3 py-1 rounded text-xs font-semibold ${sectionTab === 'all' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
          >
            All Tools
          </button>
          <button
            onClick={() => setSectionTab('credit_usage')}
            className={`px-3 py-1 rounded text-xs font-semibold ${sectionTab === 'credit_usage' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
          >
            Credit Usage
          </button>
          <button
            onClick={() => setSectionTab('credit_users')}
            className={`px-3 py-1 rounded text-xs font-semibold ${sectionTab === 'credit_users' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
          >
            Credit Users
          </button>
        </div>

        {sectionTab === 'ghl' && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">GHL Accounts (Connected Users)</h3>
                <p className="text-xs text-gray-500">Single clean view for GHL-connected accounts and quick open.</p>
              </div>
              <button
                onClick={loadGhlAccounts}
                disabled={ghlSaving || !adminReady}
                className="text-xs px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
              >
                {ghlSaving ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {ghlStatus && <div className="text-xs text-gray-400">{ghlStatus}</div>}
            <div className="flex flex-col md:flex-row gap-3">
              <input
                value={ghlSearch}
                onChange={(e) => setGhlSearch(e.target.value)}
                placeholder="Search by email and/or account name..."
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input
                value={ghlAccountNameInput}
                onChange={(e) => setGhlAccountNameInput(e.target.value)}
                placeholder="Account Name"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                value={ghlAccountEmailInput}
                onChange={(e) => setGhlAccountEmailInput(e.target.value)}
                placeholder="Email (optional)"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                value={ghlAccountLocationIdInput}
                onChange={(e) => setGhlAccountLocationIdInput(e.target.value)}
                placeholder="Location ID"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                value={ghlAccountPitInput}
                onChange={(e) => setGhlAccountPitInput(e.target.value)}
                placeholder="PIT"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveGhlAccount}
                disabled={!adminReady || ghlSaving}
                className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
              >
                {ghlSaving ? 'Saving…' : 'Save Account'}
              </button>
              <button
                onClick={() => {
                  setGhlAccountNameInput('');
                  setGhlAccountEmailInput('');
                  setGhlAccountLocationIdInput('');
                  setGhlAccountPitInput('');
                }}
                className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white text-xs"
              >
                Clear
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Account Name</th>
                    <th className="py-2 pr-4">Email (optional)</th>
                    <th className="py-2 pr-4">Location ID</th>
                    <th className="py-2 pr-4">PIT</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {ghlUsers.map((u) => (
                    <tr key={u.locationId} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{u.accountName || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{u.email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{u.locationId || '—'}</td>
                      <td className="py-2 pr-4 text-gray-400">{u.pit ? `…${u.pit.slice(-6)}` : '—'}</td>
                      <td className="py-2 pr-4">
                        <span className={`text-xs px-2 py-1 rounded ${u.active === false ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
                          {u.active === false ? 'Disabled' : 'Active'}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditGhlAccount(u)}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRunGhlTest(u)}
                            disabled={ghlTesting}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                          >
                            Test
                          </button>
                          <button
                            onClick={() => handleProvisionGhlFields(u)}
                            disabled={ghlFieldProvisioning}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                          >
                            Fields
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {ghlUsers.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={6}>
                        No GHL accounts found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-800 pt-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold">Test Payload</h4>
                <button
                  onClick={() => handleRunGhlTest()}
                  disabled={ghlTesting || !adminReady}
                  className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
                >
                  {ghlTesting ? 'Testing…' : 'Test Current Inputs'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  value={testContactName}
                  onChange={(e) => setTestContactName(e.target.value)}
                  placeholder="Contact Name"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  value={testContactEmail}
                  onChange={(e) => setTestContactEmail(e.target.value)}
                  placeholder="Email"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <input
                  value={testCompanyName}
                  onChange={(e) => setTestCompanyName(e.target.value)}
                  placeholder="Company Name"
                  className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
              </div>
              <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">
{JSON.stringify(
  {
    locationId: ghlAccountLocationIdInput.trim() || '<set location id>',
    firstName: (testContactName.trim().split(/\s+/).filter(Boolean)[0] || 'Profitibull'),
    lastName: (testContactName.trim().split(/\s+/).filter(Boolean).slice(1).join(' ') || 'Support'),
    name: testContactName.trim() || 'Profitibull Support',
    email: testContactEmail.trim() || 'support@profitibull.com',
    companyName: testCompanyName.trim() || 'Profitibull',
  },
  null,
  2
)}
              </pre>
              {testResult && (
                <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">{testResult}</pre>
              )}
            </div>
            <div className="border-t border-gray-800 pt-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold">Custom Fields (Subaccount)</h4>
                <button
                  onClick={() => handleProvisionGhlFields()}
                  disabled={ghlFieldProvisioning || !adminReady}
                  className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
                >
                  {ghlFieldProvisioning ? 'Creating…' : 'Create Missing Fields'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Uses current Location ID/PIT inputs (or account row button). One field name per line.
              </p>
              {ghlFieldProvisionStatus && (
                <div className="text-xs text-gray-300">{ghlFieldProvisionStatus}</div>
              )}
              <textarea
                value={ghlFieldNamesInput}
                onChange={(e) => setGhlFieldNamesInput(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white font-mono text-xs"
              />
              {ghlFieldProvisionResult && (
                <pre className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">{ghlFieldProvisionResult}</pre>
              )}
            </div>
          </div>
        )}

        {sectionTab === 'instantly' && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold">Instantly Data</h3>
                <p className="text-xs text-gray-500">Admin campaign analytics with historical + current event data.</p>
              </div>
              <button
                onClick={() => loadInstantlyMetrics(true)}
                disabled={instantlyLoading || !adminReady}
                className="text-xs px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
              >
                {instantlyLoading ? 'Loading…' : 'Refresh (API Sync)'}
              </button>
            </div>
            {instantlyStatus && <div className="text-xs text-gray-400">{instantlyStatus}</div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                value={instantlyFilterUserEmail}
                onChange={(e) => setInstantlyFilterUserEmail(e.target.value)}
                placeholder="User Email"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <select
                value={instantlyFilterCampaignId}
                onChange={(e) => setInstantlyFilterCampaignId(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              >
                <option value="__all__">All Campaigns</option>
                {instantlyCampaignOptions.map((campaign) => (
                  <option key={`cc-instantly-campaign-${campaign.id}`} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
              <input
                value={instantlyFilterSenderEmail}
                onChange={(e) => setInstantlyFilterSenderEmail(e.target.value)}
                placeholder="Sender Email (optional)"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                type="date"
                value={instantlyFilterStart}
                onChange={(e) => setInstantlyFilterStart(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                type="date"
                value={instantlyFilterEnd}
                onChange={(e) => setInstantlyFilterEnd(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <input
                value={instantlyLimit}
                onChange={(e) => setInstantlyLimit(e.target.value)}
                placeholder="Limit (max 50000)"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadInstantlyMetrics(true)}
                disabled={instantlyLoading || !adminReady}
                className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
              >
                Run Report (API Sync)
              </button>
              <button
                onClick={exportInstantlyReportCsv}
                disabled={instantlyLoading || (!instantlyCampaignRows.length && !instantlyDayRows.length && !instantlySenderRows.length && !instantlyPositiveReplies.length)}
                className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50 text-xs font-semibold"
                title="Export current report data to CSV files"
              >
                Export Report (CSV)
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Sends</div>
                <div className="text-white text-base font-semibold">{Number(instantlyTotals.sends ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">Filtered by selected campaign</div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Opens</div>
                <div className="text-white text-base font-semibold">{Number(instantlyTotals.opens ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {instantlyOpenRate.toFixed(2)}% open rate | {Number(instantlyUniqueTotals.unique_opens ?? 0).toLocaleString()} unique
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Clicks</div>
                <div className="text-white text-base font-semibold">{Number(instantlyTotals.clicks ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {instantlyClickRate.toFixed(2)}% click rate | {Number(instantlyUniqueTotals.unique_clicks ?? 0).toLocaleString()} unique
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                <div className="text-gray-400">Replies</div>
                <div className="text-white text-base font-semibold">{Number(instantlyTotals.replies ?? 0).toLocaleString()}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {instantlyReplyRate.toFixed(2)}% reply rate | {Number(instantlyUniqueTotals.unique_replies ?? 0).toLocaleString()} unique
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="text-xs text-gray-400">Daily Activity Trend</div>
                <div className="text-[11px] text-gray-500">Source: campaigns/analytics/daily (sends, opens, clicks, replies)</div>
              </div>
              <div className="h-64 bg-gray-950 border border-gray-800 rounded-lg p-3">
                {instantlyChartRows.length > 0 ? (
                  <div className="h-full flex flex-col">
                    <div className="flex items-center gap-4 text-[11px] text-gray-400 mb-2">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Sends</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Opens</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" />Clicks</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400" />Replies</span>
                    </div>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={instantlyChartRows} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid stroke="rgba(148,163,184,0.18)" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="day"
                          tickFormatter={(v) => formatChartDay(String(v ?? ''))}
                          tick={{ fill: '#94a3b8', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(148,163,184,0.2)' }}
                          tickLine={{ stroke: 'rgba(148,163,184,0.2)' }}
                        />
                        <YAxis
                          tickFormatter={(v) => chartValueFmt.format(Number(v ?? 0))}
                          tick={{ fill: '#94a3b8', fontSize: 11 }}
                          width={48}
                          axisLine={{ stroke: 'rgba(148,163,184,0.2)' }}
                          tickLine={{ stroke: 'rgba(148,163,184,0.2)' }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#0b1220',
                            border: '1px solid rgba(148,163,184,0.25)',
                            borderRadius: '8px',
                            color: '#e2e8f0',
                            fontSize: '12px',
                          }}
                          labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
                          formatter={(value: any, name: any) => [chartValueFmt.format(Number(value ?? 0)), name]}
                          labelFormatter={(label) => formatChartDay(String(label ?? ''))}
                        />
                        <Line type="monotone" dataKey="sends" name="Sends" stroke="#3b82f6" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="opens" name="Opens" stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#34d399" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="replies" name="Replies" stroke="#a78bfa" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-gray-500">No day-level data yet.</div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Campaign</th>
                    <th className="py-2 pr-4">Campaign ID</th>
                    <th className="py-2 pr-4">Sends</th>
                    <th className="py-2 pr-4">Unique Opens</th>
                    <th className="py-2 pr-4">Unique Clicks</th>
                    <th className="py-2 pr-4">Unique Replies</th>
                    <th className="py-2 pr-4">Bounces</th>
                    <th className="py-2 pr-4">Unsubs</th>
                  </tr>
                </thead>
                <tbody>
                  {instantlyTopCampaigns.map((row) => (
                    <tr key={`${row.campaign_id}-${row.campaign_name || ''}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.campaign_name || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.campaign_id || 'unknown'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_opens ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_clicks ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unique_replies ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.unsubscribes ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {instantlyTopCampaigns.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={8}>
                        No campaign analytics found for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto">
              <div className="text-xs text-gray-400 mb-2">Sender Performance</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Sender</th>
                    <th className="py-2 pr-4">Sends</th>
                    <th className="py-2 pr-4">Opens</th>
                    <th className="py-2 pr-4">Clicks</th>
                    <th className="py-2 pr-4">Replies</th>
                    <th className="py-2 pr-4">Bounces</th>
                  </tr>
                </thead>
                <tbody>
                  {instantlyTopSenders.map((row) => (
                    <tr key={`sender-${row.sender_email}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.sender_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.opens ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.clicks ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.replies ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {instantlyTopSenders.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={6}>
                        No sender analytics available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto">
              <div className="text-xs text-gray-400 mb-2">Reply Inbox (deduped by lead + sender)</div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Campaign</th>
                    <th className="py-2 pr-4">Lead Name</th>
                    <th className="py-2 pr-4">Lead Email</th>
                    <th className="py-2 pr-4">Sender</th>
                    <th className="py-2 pr-4">Replies</th>
                    <th className="py-2 pr-4">Interest</th>
                    <th className="py-2 pr-4">Last Reply</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instantlyPositiveReplies.map((row) => (
                    <tr key={`pos-reply-${row.reply_key}`} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-200">{row.campaign_name || row.campaign_id || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">
                        <button
                          className="text-blue-300 hover:text-blue-200 underline"
                          onClick={() => setSelectedReplyKey(row.reply_key)}
                        >
                          {row.lead_name || '—'}
                        </button>
                      </td>
                      <td className="py-2 pr-4 text-gray-300">{row.lead_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.sender_email || '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.reply_count ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{Number(row.lt_interest_status ?? 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gray-300">{row.last_reply_at ? new Date(row.last_reply_at).toLocaleString() : '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">
                        <div className="flex items-center gap-2">
                          <button
                            className={`px-2 py-1 rounded text-xs ${row.starred ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-200'}`}
                            onClick={() => toggleReplyStar(row)}
                            title={row.starred ? 'Unstar' : 'Star'}
                          >
                            {row.starred ? '★' : '☆'}
                          </button>
                          <button
                            className="px-2 py-1 rounded text-xs bg-gray-800 hover:bg-gray-700 text-gray-200"
                            onClick={() => setSelectedReplyKey(row.reply_key)}
                          >
                            Open
                          </button>
                          <button
                            className="px-2 py-1 rounded text-xs bg-red-900/60 hover:bg-red-900 text-red-100"
                            onClick={() => deleteReplyRow(row)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {instantlyPositiveReplies.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={8}>
                        No replies available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {selectedReply && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-white">{selectedReply.lead_name || selectedReply.lead_email}</div>
                    <div className="text-xs text-gray-400">
                      {selectedReply.lead_email} {selectedReply.sender_email ? `• Sender: ${selectedReply.sender_email}` : ''}
                    </div>
                  </div>
                  <button className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-200" onClick={() => setSelectedReplyKey(null)}>
                    Close
                  </button>
                </div>
                <div className="text-xs text-gray-400">
                  Campaign: {selectedReply.campaign_name || selectedReply.campaign_id || '—'} • Last Reply:{' '}
                  {selectedReply.last_reply_at ? new Date(selectedReply.last_reply_at).toLocaleString() : '—'}
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Reply Message</div>
                  <textarea
                    value={replyDraftMessage}
                    onChange={(e) => setReplyDraftMessage(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm text-gray-100"
                    placeholder="Reply body..."
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Notes</div>
                  <textarea
                    value={replyDraftNote}
                    onChange={(e) => setReplyDraftNote(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm text-gray-100"
                    placeholder="Internal notes..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveReplyEdits}
                    disabled={replySaving}
                    className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 text-xs font-semibold"
                  >
                    {replySaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => toggleReplyStar(selectedReply)}
                    className={`px-3 py-1 rounded text-xs font-semibold ${selectedReply.starred ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-100'}`}
                  >
                    {selectedReply.starred ? 'Unstar' : 'Star'}
                  </button>
                  <button
                    onClick={() => deleteReplyRow(selectedReply)}
                    className="px-3 py-1 rounded bg-red-900/70 hover:bg-red-900 text-red-100 text-xs font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {sectionTab === 'credit_usage' && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">System Credit Usage</h3>
                <p className="text-xs text-gray-500">Seamless license totals and charge-pattern signals.</p>
              </div>
              <button
                onClick={loadCreditUsage}
                disabled={!adminReady || creditUsageLoading}
                className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50 text-xs font-semibold"
              >
                {creditUsageLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {creditUsageStatus && <div className="text-xs text-gray-400">{creditUsageStatus}</div>}
            {!creditUsage ? (
              <div className="text-sm text-gray-500">No usage data loaded yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2">
                    <div className="text-gray-400 text-xs">Seamless</div>
                    <div className="text-gray-300">Last 24h: <span className="text-white font-semibold">{creditUsage.seamless.credits24h}</span></div>
                    <div className="text-gray-300">All Time: <span className="text-white font-semibold">{creditUsage.seamless.creditsAllTime ?? 0}</span></div>
                    <div className="text-gray-300">Used Since 00:01 UTC: <span className="text-white font-semibold">{creditUsage.seamless.creditsSinceResetUtc}</span></div>
                    <div className="text-gray-300">Daily Limit (1,000 x active licenses): <span className="text-white font-semibold">{creditUsage.seamless.dailyCreditLimit}</span></div>
                    <div className="text-gray-300">Remaining Today (UTC): <span className="text-white font-semibold">{creditUsage.seamless.remainingTodayUtc}</span></div>
                    <div className="text-gray-300">Reset Window Start: <span className="text-white font-semibold">{creditUsage.seamless.resetAtUtc}</span></div>
                    <div className="text-gray-300">Month to Date: <span className="text-white font-semibold">{creditUsage.seamless.creditsMonthToDate}</span></div>
                    <div className="text-gray-300">Licenses: <span className="text-white font-semibold">{creditUsage.seamless.licenses.length}</span></div>
                    <div className="text-gray-300">MTD Charge Events: <span className="text-white font-semibold">{creditUsage.seamless.chargeEventsMonthToDate ?? 0}</span></div>
                    <div className="text-gray-300">MTD Unique Entities: <span className="text-white font-semibold">{creditUsage.seamless.uniqueEntitiesMonthToDate ?? 0}</span></div>
                    <div className="text-gray-300">MTD Repeat Charges: <span className="text-white font-semibold">{creditUsage.seamless.repeatChargesMonthToDate ?? 0}</span></div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2">
                    <div className="text-gray-400 text-xs">Apollo</div>
                    <div className="text-gray-300">Last 24h: <span className="text-white font-semibold">{creditUsage.apollo.credits24h}</span></div>
                    <div className="text-gray-300">Month to Date: <span className="text-white font-semibold">{creditUsage.apollo.creditsMonthToDate}</span></div>
                    <div className="text-gray-300">Cap: <span className="text-white font-semibold">{creditUsage.apollo.monthlyCreditCap}</span></div>
                    <div className="text-gray-300">Remaining: <span className="text-white font-semibold">{creditUsage.apollo.remainingThisMonth}</span></div>
                  </div>
                </div>

                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3">Calendar Day History (UTC)</h4>
                  <div className="overflow-x-auto max-h-80">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-800">
                          <th className="py-2 pr-4">Date (UTC)</th>
                          <th className="py-2 pr-4">Total Credits</th>
                          <th className="py-2 pr-4">Seamless</th>
                          <th className="py-2 pr-4">Apollo</th>
                          <th className="py-2 pr-4">Total Events</th>
                          <th className="py-2 pr-4">Seamless Events</th>
                          <th className="py-2 pr-4">Apollo Events</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(creditUsage.calendarDayHistoryUtc ?? [])
                          .slice()
                          .sort((a, b) => b.dateUtc.localeCompare(a.dateUtc))
                          .map((day) => (
                            <tr key={day.dateUtc} className="border-b border-gray-900">
                              <td className="py-2 pr-4 text-gray-200">{day.dateUtc}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.creditsTotal ?? 0}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.creditsSeamless ?? 0}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.creditsApollo ?? 0}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.eventsTotal ?? 0}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.eventsSeamless ?? 0}</td>
                              <td className="py-2 pr-4 text-gray-200">{day.eventsApollo ?? 0}</td>
                            </tr>
                          ))}
                        {(creditUsage.calendarDayHistoryUtc ?? []).length === 0 && (
                          <tr>
                            <td className="py-3 text-gray-500" colSpan={7}>No calendar-day usage data yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3">Seamless Licenses</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-800">
                          <th className="py-2 pr-4">License ID</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">24h</th>
                          <th className="py-2 pr-4">Since 00:01 UTC</th>
                          <th className="py-2 pr-4">Remaining Today</th>
                          <th className="py-2 pr-4">MTD</th>
                          <th className="py-2 pr-4">All Time</th>
                          <th className="py-2 pr-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditUsage.seamless.licenses.map((license) => (
                          <tr key={`${license.id}-${license.email}`} className="border-b border-gray-900">
                            <td className="py-2 pr-4 text-gray-200">{license.id || '—'}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.email || '—'}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.credits24h ?? 0}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.creditsSinceResetUtc ?? 0}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.remainingTodayUtc ?? 0}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.creditsMonthToDate ?? 0}</td>
                            <td className="py-2 pr-4 text-gray-200">{license.creditsAllTime ?? 0}</td>
                            <td className="py-2 pr-4 text-gray-300">{license.active ? 'Active' : 'Inactive'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-3">Seamless Source Breakdown (MTD)</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-gray-800">
                            <th className="py-2 pr-4">Source</th>
                            <th className="py-2 pr-4">Events</th>
                            <th className="py-2 pr-4">Credits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(creditUsage.seamless.bySourceMonthToDate ?? []).map((item) => (
                            <tr key={item.source} className="border-b border-gray-900">
                              <td className="py-2 pr-4 text-gray-200">{item.source}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.eventsMonthToDate}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.creditsMonthToDate}</td>
                            </tr>
                          ))}
                          {(creditUsage.seamless.bySourceMonthToDate ?? []).length === 0 && (
                            <tr>
                              <td className="py-3 text-gray-500" colSpan={3}>No MTD source activity.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-3">Seamless Trigger Breakdown (MTD)</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-gray-800">
                            <th className="py-2 pr-4">Trigger</th>
                            <th className="py-2 pr-4">Events</th>
                            <th className="py-2 pr-4">Credits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(creditUsage.seamless.byTriggerReasonMonthToDate ?? []).map((item) => (
                            <tr key={item.triggerReason} className="border-b border-gray-900">
                              <td className="py-2 pr-4 text-gray-200">{item.triggerReason}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.eventsMonthToDate}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.creditsMonthToDate}</td>
                            </tr>
                          ))}
                          {(creditUsage.seamless.byTriggerReasonMonthToDate ?? []).length === 0 && (
                            <tr>
                              <td className="py-3 text-gray-500" colSpan={3}>No MTD trigger activity.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {sectionTab === 'credit_users' && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Credit Users</h3>
                <p className="text-xs text-gray-500">Who is consuming credits, with Seamless and total views.</p>
              </div>
              <button
                onClick={() => {
                  void loadCreditUsage();
                }}
                disabled={!adminReady || creditUsageLoading}
                className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50 text-xs font-semibold"
              >
                {creditUsageLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {creditUsageStatus && <div className="text-xs text-gray-400">{creditUsageStatus}</div>}
            {creditUserSchedulesStatus && <div className="text-xs text-gray-400">{creditUserSchedulesStatus}</div>}
            <input
              value={creditUserSearch}
              onChange={(e) => setCreditUserSearch(e.target.value)}
              placeholder="Filter by user email..."
              className="w-full md:w-96 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm"
            />
            {!creditUsage ? (
              <div className="text-sm text-gray-500">No usage data loaded yet.</div>
            ) : (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-800">
                        <th className="py-2 pr-4">User</th>
                        <th className="py-2 pr-4">Schedules</th>
                        <th className="py-2 pr-4">Seamless 24h</th>
                        <th className="py-2 pr-4">Seamless MTD</th>
                        <th className="py-2 pr-4">Seamless All Time</th>
                        <th className="py-2 pr-4">Total 24h</th>
                        <th className="py-2 pr-4">Total MTD</th>
                        <th className="py-2 pr-4">Total All Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCreditUsers.map((item) => {
                        const email = String(item.email || '').trim().toLowerCase();
                        const userSchedules = creditUserSchedulesByEmail.get(email) ?? [];
                        const isExpanded = Boolean(expandedCreditUsers[email]);
                        const isSchedulesLoading = Boolean(creditUserSchedulesLoadingByEmail[email]);
                        const isSchedulesLoaded = Boolean(creditUserSchedulesLoadedByEmail[email]);
                        return (
                          <Fragment key={`group-${item.email}`}>
                            <tr key={`row-${item.email}`} className="border-b border-gray-900">
                              <td className="py-2 pr-4 text-gray-200">{item.email}</td>
                              <td className="py-2 pr-4 text-gray-200">
                                <button
                                  onClick={() => {
                                    if (isExpanded) {
                                      setExpandedCreditUsers((prev) => ({ ...prev, [email]: false }));
                                      return;
                                    }
                                    setExpandedCreditUsers((prev) => ({ ...prev, [email]: true }));
                                    if (!isSchedulesLoaded && !isSchedulesLoading) {
                                      void loadCreditUserSchedulesForEmail(email);
                                    }
                                  }}
                                  disabled={isSchedulesLoading}
                                  className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-white disabled:opacity-50"
                                >
                                  {isSchedulesLoading ? 'Loading…' : isExpanded ? 'Hide' : isSchedulesLoaded ? `Show (${userSchedules.length})` : 'Show'}
                                </button>
                              </td>
                              <td className="py-2 pr-4 text-gray-200">{item.seamlessCredits24h}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.seamlessCreditsMonthToDate}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.seamlessCreditsAllTime}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.credits24h}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.creditsMonthToDate}</td>
                              <td className="py-2 pr-4 text-gray-200">{item.creditsAllTime}</td>
                            </tr>
                            {isExpanded && (
                              <tr key={`details-${item.email}`} className="border-b border-gray-900">
                                <td className="py-2 px-2 bg-gray-950" colSpan={8}>
                                  {isSchedulesLoading ? (
                                    <div className="text-xs text-gray-500">Loading schedules…</div>
                                  ) : !isSchedulesLoaded ? (
                                    <div className="text-xs text-gray-500">Schedule details are loaded on demand.</div>
                                  ) : userSchedules.length === 0 ? (
                                    <div className="text-xs text-gray-500">No schedules found for this user.</div>
                                  ) : (
                                    <div className="space-y-2">
                                      {userSchedules.map((s) => (
                                        <div
                                          key={`${s._env}-${s.userEmail}-${s.scheduleId}`}
                                          className="rounded border border-gray-800 bg-gray-900 px-3 py-2 text-xs text-gray-200 flex flex-wrap items-center gap-3"
                                        >
                                          <span className="text-gray-400">[{s._env}]</span>
                                          <span className="font-semibold text-white">{s.name || s.scheduleId || 'Unnamed'}</span>
                                          <span className={s.status === 'ACTIVE' ? 'text-green-400' : 'text-yellow-400'}>
                                            {s.status || 'UNKNOWN'}
                                          </span>
                                          <span>
                                            Next: {s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : '—'}
                                          </span>
                                          <span>
                                            Days: {Array.isArray(s.scheduleDays) && s.scheduleDays.length > 0 ? s.scheduleDays.join(', ') : '—'}
                                          </span>
                                          <span>
                                            Time: {formatScheduleTime(s.scheduleHour, s.scheduleMinute)}
                                          </span>
                                          <span>
                                            TZ: {s.timezone || '—'}
                                          </span>
                                          <span>
                                            Credit Limit: {typeof s.creditLimit === 'number' ? s.creditLimit : '—'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                      {filteredCreditUsers.length === 0 && (
                        <tr>
                          <td className="py-3 text-gray-500" colSpan={8}>No users matched this filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {sectionTab === 'all' && (
          <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-3">
            <h3 className="text-lg font-semibold">Admin Session</h3>
            <div className="text-sm text-gray-400">Signed in as</div>
            <div className="text-white font-semibold">{userEmail || '—'}</div>
            <div className={`text-xs ${adminToken ? 'text-green-400' : 'text-red-400'}`}>
              {adminToken ? 'Admin token active' : 'Admin token missing'}
            </div>
            <div className="text-xs text-gray-500">No password re-entry required.</div>
          </div>

          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4 lg:col-span-2">
            <h3 className="text-lg font-semibold">User Lookup</h3>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                placeholder="target@company.com"
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
              <button
                onClick={handleLookup}
                disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold disabled:opacity-50"
              >
                Load User
              </button>
              <button
                onClick={refreshUser}
                disabled={!user || loading}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
              >
                Refresh
              </button>
            </div>
            {status && (
              <div
                className={`text-sm px-3 py-2 rounded-lg ${
                  status.type === 'error'
                    ? 'bg-red-900/40 text-red-300'
                    : status.type === 'success'
                    ? 'bg-green-900/40 text-green-300'
                    : 'bg-gray-800 text-gray-300'
                }`}
              >
                {status.message}
              </div>
            )}
            {user && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2">
                  <div className="text-gray-400 text-xs">User</div>
                  <div className="font-semibold">{user.email}</div>
                  <div className="text-gray-400">Credits: <span className="text-white">{user.credits}</span></div>
                  <div className="text-gray-400">Contact Credits: <span className="text-white">{user.contactCredits ?? 0}</span></div>
                  <div className="text-gray-400">Daily Credit Limit: <span className="text-white">{user.dailyCreditsLimit ?? '—'}</span></div>
                  <div className="text-gray-400">Daily Contact Limit: <span className="text-white">{user.dailyContactCreditsLimit ?? '—'}</span></div>
                  <div className="text-gray-400">Contact Reset: <span className="text-white">{user.contactCreditsResetAt ?? '—'}</span></div>
                  <div className="text-gray-400">Assigned Instantly Campaigns: <span className="text-white">{(user.instantlyCampaignIds ?? []).length}</span></div>
                  <div className="text-gray-400">Timezone: <span className="text-white">{user.timezone}</span></div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2">
                  <div className="text-gray-400 text-xs">Status</div>
                  <div className="text-gray-400">Admin: <span className="text-white">{user.isAdmin ? 'Yes' : 'No'}</span></div>
                  <div className="text-gray-400">Disabled: <span className="text-white">{user.disabled ? 'Yes' : 'No'}</span></div>
                  <div className="text-gray-400">Schedules: <span className="text-white">{user.scheduleCount}</span></div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400 text-xs">Webhook</div>
                    <span className={`text-xs px-2 py-1 rounded ${user.webhook ? 'bg-green-900/40 text-green-300' : 'bg-gray-800 text-gray-400'}`}>
                      {user.webhook ? 'Active' : 'Off'}
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row gap-2">
                    <input
                      value={webhookInput}
                      onChange={(e) => setWebhookInput(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                    <button
                      onClick={handleSetWebhook}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                    >
                      Save Webhook
                    </button>
                  </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400 text-xs">Global CRM Fallback (Per User)</div>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        user.ghlPit && user.ghlLocationId ? 'bg-green-900/40 text-green-300' : 'bg-gray-800 text-gray-400'
                      }`}
                    >
                      {user.ghlPit && user.ghlLocationId ? 'Configured' : 'Using Global Default'}
                    </span>
                  </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      value={ghlPitInput}
                      onChange={(e) => setGhlPitInput(e.target.value)}
                      placeholder="pit-..."
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                    <input
                      value={ghlLocationIdInput}
                      onChange={(e) => setGhlLocationIdInput(e.target.value)}
                      placeholder="Location ID"
                      className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-800">
                      <input
                        value={linkedinCookieInput}
                        onChange={(e) => setLinkedinCookieInput(e.target.value)}
                        placeholder="LinkedIn Connection Cookie"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <input
                        value={instagramCookieInput}
                        onChange={(e) => setInstagramCookieInput(e.target.value)}
                        placeholder="Instagram Connection Cookie"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <input
                        value={linkedinCommentAgentInput}
                        onChange={(e) => setLinkedinCommentAgentInput(e.target.value)}
                        placeholder="LinkedIn Comment Agent ID"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <input
                        value={instagramCommentAgentInput}
                        onChange={(e) => setInstagramCommentAgentInput(e.target.value)}
                        placeholder="Instagram Comment Agent ID"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <input
                        value={pbConnectorIdLiInput}
                        onChange={(e) => setPbConnectorIdLiInput(e.target.value)}
                        placeholder="PBConnectorIDLI"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <input
                        value={pbConnectorIdIgInput}
                        onChange={(e) => setPbConnectorIdIgInput(e.target.value)}
                        placeholder="PBConnectorIDIG"
                        className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                    </div>
                    <div className="pt-1 border-t border-gray-800 space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <div className="text-gray-400 text-xs">Assigned GHL Integrations</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Select one or more managed GHL accounts this user can send to.
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={loadGhlAccounts}
                            disabled={ghlSaving || ghlAssignmentSaving}
                            className="px-3 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                          >
                            Refresh Accounts
                          </button>
                          <button
                            onClick={handleSaveGhlLocationAssignments}
                            disabled={ghlAssignmentSaving}
                            className="px-3 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                          >
                            {ghlAssignmentSaving ? 'Saving…' : 'Save Assignments'}
                          </button>
                        </div>
                      </div>
                      {ghlAssignmentStatus && <div className="text-xs text-gray-400">{ghlAssignmentStatus}</div>}
                      <div className="max-h-48 overflow-y-auto rounded border border-gray-800 bg-gray-950 p-2 space-y-2">
                        {ghlAccounts.map((account) => {
                          const locationId = String(account.locationId ?? '').trim();
                          const checked = ghlAssignmentSelectedIds.includes(locationId);
                          return (
                            <label
                              key={`user-ghl-assignment-${locationId}`}
                              className="flex items-center justify-between gap-3 px-2 py-1 rounded hover:bg-gray-900"
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-gray-200 truncate">
                                  {account.accountName || locationId}
                                </div>
                                <div className="text-[11px] text-gray-500 truncate">
                                  {locationId}
                                  {account.email ? ` · ${account.email}` : ''}
                                </div>
                              </div>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleGhlLocationAssignment(locationId)}
                                className="h-4 w-4 accent-red-500"
                              />
                            </label>
                          );
                        })}
                        {ghlAccounts.length === 0 && (
                          <div className="px-2 py-2 text-xs text-gray-500">No GHL accounts loaded yet.</div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="text-xs text-gray-500 flex items-center">
                          Default location used when no explicit target is sent.
                        </div>
                        <select
                          value={ghlAssignmentDefaultLocationId}
                          onChange={(e) => setGhlAssignmentDefaultLocationId(e.target.value)}
                          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        >
                          <option value="">No default</option>
                          {ghlAssignmentSelectedIds.map((locationId) => {
                            const account = ghlAccounts.find((item) => String(item.locationId ?? '').trim() === locationId);
                            const label = account?.accountName ? `${account.accountName} (${locationId})` : locationId;
                            return (
                              <option key={`default-ghl-${locationId}`} value={locationId}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleSetGhlConfig}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                      >
                        Save CRM + Connectors
                      </button>
                    </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2 md:col-span-2">
                  <div className="text-gray-400 text-xs">Last Login</div>
                  <div className="text-gray-400">At: <span className="text-white">{user.lastLoginAt ?? '—'}</span></div>
                  <div className="text-gray-400">IP: <span className="text-white">{user.lastLoginIp ?? '—'}</span></div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400 text-xs">User Type</div>
                    <button
                      onClick={handleSetUserType}
                      disabled={loading}
                      className="px-3 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                    >
                      Save Type
                    </button>
                  </div>
                  {userTypeStatus && <div className="text-xs text-gray-400">{userTypeStatus}</div>}
                  <div className="flex flex-col md:flex-row gap-3">
                    <select
                      value={userTypeChoice}
                      onChange={(e) => setUserTypeChoice(e.target.value as 'user' | 'admin' | 'free-trial')}
                      className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="user">User</option>
                      <option value="free-trial">Free Trial</option>
                      <option value="admin">Admin</option>
                    </select>
                    <div className="text-xs text-gray-400 flex items-center">
                      Free-trial disables Custom KPI, Content Scraping, ProfitPod, Business Intel, PEPai Outreach, and Scheduled Searches.
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400 text-xs">Feature Access</div>
                    <button
                      onClick={handleSetFeatures}
                      disabled={loading}
                      className="px-3 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                    >
                      Save Access
                    </button>
                  </div>
                  {featureStatus && <div className="text-xs text-gray-400">{featureStatus}</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">Custom KPI Search</span>
                      <input
                        type="checkbox"
                        checked={featureCustomKpi}
                        onChange={(e) => setFeatureCustomKpi(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">Content Scraping</span>
                      <input
                        type="checkbox"
                        checked={featureContentScraping}
                        onChange={(e) => setFeatureContentScraping(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">ProfitPod</span>
                      <input
                        type="checkbox"
                        checked={featureProfitPod}
                        onChange={(e) => setFeatureProfitPod(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">Business Intel</span>
                      <input
                        type="checkbox"
                        checked={featureBusinessIntel}
                        onChange={(e) => setFeatureBusinessIntel(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">PEPai Outreach</span>
                      <input
                        type="checkbox"
                        checked={featurePepaiResearch}
                        onChange={(e) => setFeaturePepaiResearch(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-gray-300">Email Workspace</span>
                      <input
                        type="checkbox"
                        checked={featureEmailWorkspace}
                        onChange={(e) => setFeatureEmailWorkspace(e.target.checked)}
                        className="h-4 w-4 accent-red-500"
                      />
                    </label>
                  </div>
                  <div className="text-xs text-gray-500">
                    The Research checkbox is always visible for `@profitibull.com` users. Everyone else needs this access enabled.
                  </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3 md:col-span-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-gray-400 text-xs">Instantly Campaign Assignments</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Controls campaigns available to this user in Email → Data.
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          setInstantlyAssignmentLoading(true);
                          setInstantlyAssignmentStatus(null);
                          try {
                            const campaigns = await loadInstantlyCampaignOptionsForAssignments(true);
                            setInstantlyAssignmentStatus(`Loaded ${campaigns.length} campaign option${campaigns.length === 1 ? '' : 's'}.`);
                          } catch (error: any) {
                            setInstantlyAssignmentStatus(error?.message || 'Failed to load campaign options.');
                          } finally {
                            setInstantlyAssignmentLoading(false);
                          }
                        }}
                        disabled={instantlyAssignmentLoading}
                        className="px-3 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                      >
                        Refresh Campaigns
                      </button>
                      <button
                        onClick={handleSaveInstantlyCampaignAssignments}
                        disabled={instantlyAssignmentLoading}
                        className="px-3 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                      >
                        Save Assignments
                      </button>
                    </div>
                  </div>
                  {instantlyAssignmentStatus && <div className="text-xs text-gray-400">{instantlyAssignmentStatus}</div>}
                  <div className="max-h-64 overflow-y-auto rounded border border-gray-800 bg-gray-950 p-2 space-y-2">
                    {instantlyCampaignOptions.map((campaign) => {
                      const checked = instantlyAssignmentSelectedIds.includes(campaign.id);
                      return (
                        <label
                          key={`user-assignment-campaign-${campaign.id}`}
                          className="flex items-center justify-between gap-3 px-2 py-1 rounded hover:bg-gray-900"
                        >
                          <div className="min-w-0">
                            <div className="text-sm text-gray-200 truncate">{campaign.name}</div>
                            <div className="text-[11px] text-gray-500 truncate">{campaign.id}</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInstantlyCampaignAssignment(campaign.id)}
                            className="h-4 w-4 accent-red-500"
                          />
                        </label>
                      );
                    })}
                    {instantlyCampaignOptions.length === 0 && (
                      <div className="px-2 py-2 text-xs text-gray-500">No campaign options loaded yet.</div>
                    )}
                  </div>
                  {missingAssignedCampaignIds.length > 0 && (
                    <div className="text-xs text-amber-300">
                      Assigned IDs missing from current Instantly API list: {missingAssignedCampaignIds.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Users</h3>
            <button
              onClick={loadUserList}
              disabled={loading || !adminReady}
              className="text-xs px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Refresh List'}
            </button>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              placeholder="Search by email..."
              className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            />
            <select
              value={listSort}
              onChange={(e) => setListSort(e.target.value as any)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            >
              <option value="credits">Credits</option>
              <option value="lastLoginAt">Last Login</option>
              <option value="scheduleCount">Schedules</option>
              <option value="email">Email</option>
            </select>
            <button
              onClick={() => setListSortDir(listSortDir === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
            >
              {listSortDir === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Credits</th>
                  <th className="py-2 pr-4">Timezone</th>
                  <th className="py-2 pr-4">Schedules</th>
                  <th className="py-2 pr-4">Last Login</th>
                  <th className="py-2 pr-4">Webhook</th>
                  <th className="py-2 pr-4">GHL Override</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUserList.map((u) => (
                  <tr key={u.email} className="border-b border-gray-900">
                    <td className="py-2 pr-4 text-gray-200">{u.email}</td>
                    <td className="py-2 pr-4 text-gray-200">{u.credits ?? 0}</td>
                    <td className="py-2 pr-4 text-gray-400">{u.timezone ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{u.scheduleCount ?? 0}</td>
                    <td className="py-2 pr-4 text-gray-400">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                    <td className="py-2 pr-4">
                      <span className={`text-xs px-2 py-1 rounded ${u.webhook ? 'bg-green-900/40 text-green-300' : 'bg-gray-800 text-gray-400'}`}>
                        {u.webhook ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          u.ghlPit && u.ghlLocationId ? 'bg-green-900/40 text-green-300' : 'bg-gray-800 text-gray-400'
                        }`}
                      >
                        {u.ghlPit && u.ghlLocationId ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`text-xs px-2 py-1 rounded ${u.disabled ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
                        {u.disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => handleSelectUser(u.email)}
                        className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredUserList.length === 0 && (
                  <tr>
                    <td className="py-4 text-gray-500" colSpan={9}>
                      No users loaded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Scheduled Searches</h3>
              <p className="text-xs text-gray-500">Search by user email and sort by next run.</p>
            </div>
            <button
              onClick={loadSchedules}
              disabled={scheduleLoading || !adminReady}
              className="text-xs px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
            >
              {scheduleLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <select
              value={scheduleEnv}
              onChange={(e) => setScheduleEnv(e.target.value as any)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            >
              <option value="prod">Prod</option>
              <option value="dev">Dev</option>
              <option value="all">All</option>
            </select>
            <input
              value={scheduleSearch}
              onChange={(e) => setScheduleSearch(e.target.value)}
              placeholder="Filter by user email..."
              className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            />
            {scheduleEnv !== 'prod' && (
              <input
                type="password"
                value={scheduleAdminPassword}
                onChange={(e) => setScheduleAdminPassword(e.target.value)}
                placeholder="Admin password (dev)"
                className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            )}
            <select
              value={scheduleSort}
              onChange={(e) => setScheduleSort(e.target.value as any)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            >
              <option value="nextRunAt">Next Run</option>
              <option value="createdAt">Created</option>
              <option value="userEmail">User</option>
            </select>
            <button
              onClick={() => setScheduleSortDir(scheduleSortDir === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
            >
              {scheduleSortDir === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
          {scheduleStatus && (
            <div className="text-xs text-gray-400">{scheduleStatus}</div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="py-2 pr-4">Env</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Days</th>
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4">Timezone</th>
                  <th className="py-2 pr-4">Next Run</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">page_current_lastPage</th>
                  <th className="py-2 pr-4">total_pages</th>
                  <th className="py-2 pr-4">credits_used</th>
                  <th className="py-2 pr-4">Credit Limit</th>
                  <th className="py-2 pr-4">Enabled</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSchedules.map((item, index) => (
                  <tr key={item.scheduleId ?? item.id ?? index} className="border-b border-gray-900">
                    {(() => {
                      const metric = scheduleMetricsBySchedule.get(String(item.scheduleId ?? '').trim());
                      const pageCurrentLastPage =
                        metric?.lastPage ?? (Number.isFinite(Number(item.lastPage)) ? Number(item.lastPage) : null);
                      return (
                        <>
                    <td className="py-2 pr-4 text-gray-400">{item._env ?? 'prod'}</td>
                    <td className="py-2 pr-4 text-gray-200">{item.userEmail ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-200">{item.name ?? item.scheduleName ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">
                      {Array.isArray(item.scheduleDays) ? item.scheduleDays.join(', ') : '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-400">{formatScheduleTime(item.scheduleHour, Number(item.scheduleMinute ?? 0))}</td>
                    <td className="py-2 pr-4 text-gray-400">{item.timezone ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">
                      {item.nextRunAt ? new Date(item.nextRunAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-400">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-400">{pageCurrentLastPage ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{metric?.totalPages ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{metric?.creditsUsed ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{item.creditLimit ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-400">{item.enabled ? 'Yes' : 'No'}</td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white"
                          onClick={async () => {
                            try {
                              await updateSchedule(item, { enabled: !item.enabled });
                              await loadSchedules();
                            } catch (err: any) {
                              setScheduleStatus(err?.message || 'Update failed.');
                            }
                          }}
                        >
                          {item.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded bg-red-700 hover:bg-red-600 text-white"
                          onClick={async () => {
                            if (!confirm('Delete this schedule?')) return;
                            try {
                              await deleteSchedule(item);
                              await loadSchedules();
                            } catch (err: any) {
                              setScheduleStatus(err?.message || 'Delete failed.');
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
                {sortedSchedules.length === 0 && (
                  <tr>
                    <td className="py-4 text-gray-500" colSpan={14}>
                      No schedules loaded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-800 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold">Run History</h4>
                <p className="text-xs text-gray-500">All runs (success + error) with CSV download.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setScheduleRunTab('runs');
                    loadScheduleRuns('runs');
                  }}
                  className={`text-xs px-3 py-1 rounded ${scheduleRunTab === 'runs' ? 'bg-blue-600' : 'bg-gray-800'} text-white`}
                >
                  All Runs
                </button>
                <button
                  onClick={() => {
                    setScheduleRunTab('errors');
                    loadScheduleRuns('errors');
                  }}
                  className={`text-xs px-3 py-1 rounded ${scheduleRunTab === 'errors' ? 'bg-red-600' : 'bg-gray-800'} text-white`}
                >
                  Errors
                </button>
                <button
                  onClick={() => loadScheduleRuns(scheduleRunTab)}
                  disabled={scheduleRunLoading || !adminReady}
                  className="text-xs px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                >
                  {scheduleRunLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>
            {scheduleRunStatus && (
              <div className="text-xs text-gray-400">{scheduleRunStatus}</div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Env</th>
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Schedule</th>
                    <th className="py-2 pr-4">Started</th>
                    <th className="py-2 pr-4">Saved</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">CSV</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScheduleRuns.map((item, index) => (
                    <tr key={item.runId ?? index} className="border-b border-gray-900">
                      <td className="py-2 pr-4 text-gray-400">{item._env ?? 'prod'}</td>
                      <td className="py-2 pr-4 text-gray-200">{item.userEmail ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-200">{item.scheduleId ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-400">
                        {item.startedAt ? new Date(item.startedAt).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 pr-4 text-gray-400">{item.savedCount ?? 0}</td>
                      <td className={`py-2 pr-4 ${String(item.status).toUpperCase() === 'ERROR' ? 'text-red-400' : 'text-green-400'}`}>
                        {item.status ?? '—'}
                      </td>
                      <td className="py-2 pr-4">
                        {item.csvUrl ? (
                          <a
                            href={item.csvUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            Download
                          </a>
                        ) : (
                          <span className="text-gray-500 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {sortedScheduleRuns.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={7}>
                        No run logs loaded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Create User</h3>
            <span className="text-xs text-gray-500">Admin only</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-2">Email</label>
              <input
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="new@company.com"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Temp Password (optional)</label>
              <input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Credits</label>
              <input
                value={createCredits}
                onChange={(e) => setCreateCredits(e.target.value)}
                placeholder="200"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Timezone</label>
              <input
                value={createTimezone}
                onChange={(e) => setCreateTimezone(e.target.value)}
                placeholder="America/Chicago"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">User Type</label>
              <select
                value={createUserType}
                onChange={(e) => setCreateUserType(e.target.value as 'user' | 'admin' | 'free-trial')}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              >
                <option value="user">User</option>
                <option value="free-trial">Free Trial</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={createIsAdmin}
                onChange={(e) => setCreateIsAdmin(e.target.checked)}
              />
              <span className="text-gray-300">Admin</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={createDisabled}
                onChange={(e) => setCreateDisabled(e.target.checked)}
              />
              <span className="text-gray-300">Disabled</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCreateUser}
              disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold disabled:opacity-50"
            >
              Create User
            </button>
          </div>
        </div>

        {user && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-semibold">Credits</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    value={creditAdd}
                    onChange={(e) => setCreditAdd(e.target.value)}
                    placeholder="Add credits"
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                  <button
                    onClick={handleAddCredits}
                    disabled={loading}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    value={creditSet}
                    onChange={(e) => setCreditSet(e.target.value)}
                    placeholder="Set credits"
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                  <button
                    onClick={handleSetCredits}
                    disabled={loading}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                  >
                    Set
                  </button>
                </div>
                <div className="pt-2 border-t border-gray-800">
                  <div className="text-xs text-gray-400 mb-2">Contact Credits</div>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={contactCreditAdd}
                      onChange={(e) => setContactCreditAdd(e.target.value)}
                      placeholder="Add contact credits"
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                    />
                    <button
                      onClick={handleAddContactCredits}
                      disabled={loading}
                      className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={contactCreditSet}
                      onChange={(e) => setContactCreditSet(e.target.value)}
                      placeholder="Set contact credits"
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                    />
                    <button
                      onClick={handleSetContactCredits}
                      disabled={loading}
                      className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                    >
                      Set
                    </button>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-800">
                  <div className="text-xs text-gray-400 mb-2">Daily Limits (Non-@profitibull.com)</div>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={dailyCreditLimitInput}
                      onChange={(e) => setDailyCreditLimitInput(e.target.value)}
                      placeholder="Daily credit limit"
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                    />
                    <input
                      value={dailyContactCreditLimitInput}
                      onChange={(e) => setDailyContactCreditLimitInput(e.target.value)}
                      placeholder="Daily contact limit"
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                    />
                  </div>
                  <button
                    onClick={handleSetDailyLimits}
                    disabled={loading}
                    className="w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                  >
                    Save Daily Limits
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-semibold">Account</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                  <button
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                  >
                    Reset
                  </button>
                </div>
                <button
                  onClick={handleToggleDisabled}
                  disabled={loading}
                  className={`w-full px-3 py-2 rounded-lg text-white ${
                    user.disabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50`}
                >
                  {user.disabled ? 'Enable User' : 'Disable User'}
                </button>
              </div>
            </div>

            <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-semibold">Activity</h3>
              <div className="flex gap-2">
                <input
                  value={activityLimit}
                  onChange={(e) => setActivityLimit(e.target.value)}
                  placeholder="Limit"
                  className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                />
                <button
                  onClick={handleLoadActivity}
                  disabled={loading}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
                >
                  Load
                </button>
              </div>
              <div className="text-xs text-gray-500">Shows latest credit ledger entries.</div>
            </div>
          </div>
        )}

        {user && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Credit Ledger</h3>
            {activity.length === 0 ? (
              <div className="text-sm text-gray-500">No activity loaded.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-800">
                      <th className="py-2 pr-4">Time</th>
                      <th className="py-2 pr-4">Amount</th>
                      <th className="py-2 pr-4">Source</th>
                      <th className="py-2 pr-4">ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((item, index) => (
                      <tr key={item.id ?? item.createdAt ?? index} className="border-b border-gray-900">
                        <td className="py-2 pr-4 text-gray-300">{item.createdAt ?? '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{item.amount ?? '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{item.source ?? '—'}</td>
                        <td className="py-2 pr-4 text-gray-500">{item.id ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Automation Station</h3>
              <p className="text-xs text-gray-500">Inbound/outbound logs, errors, and website requests.</p>
            </div>
            <span className="text-xs text-gray-500">
              {websiteRequestsReady ? '@profitibull.com + admin tools' : 'Admin only'}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {(['inbound', 'outbound', 'errors', 'website_requests'] as const)
              .filter((tab) => (isAdmin ? true : tab === 'website_requests'))
              .map((tab) => (
              <button
                key={tab}
                onClick={() => setLogTab(tab as any)}
                className={`px-3 py-1 rounded text-xs font-semibold ${logTab === tab ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300'}`}
              >
                {tab === 'inbound'
                  ? 'Webhooks Inbound'
                  : tab === 'outbound'
                    ? 'Webhooks Outbound'
                    : tab === 'errors'
                      ? 'System Errors'
                      : 'Website Requests'}
              </button>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
              placeholder="Search logs..."
              className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            />
            <input
              value={logLimit}
              onChange={(e) => setLogLimit(e.target.value)}
              placeholder="Limit"
              className="w-28 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
            />
            <button
              onClick={() => loadLogs()}
              disabled={logLoading || !(logTab === 'website_requests' ? websiteRequestsReady : isAdmin)}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50"
            >
              {logLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {logStatus && (
            <div className="text-xs text-gray-400">{logStatus}</div>
          )}

          {logTab === 'website_requests' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Company Name</th>
                    <th className="py-2 pr-4">Email Address</th>
                    <th className="py-2 pr-4">Titles Requested</th>
                    <th className="py-2 pr-4">Companies Requested</th>
                    <th className="py-2 pr-4">Email Sent Date</th>
                    <th className="py-2 pr-4">PDF of Results</th>
                    <th className="py-2 pr-4">Save</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWebsiteRequests.map((item, index) => {
                    const requestId = String(item.requestId ?? '');
                    const draft = websiteDrafts[requestId] ?? {
                      emailSentDate: String(item.emailSentDate ?? ''),
                      pdfUrl: String(item.pdfUrl ?? ''),
                    };
                    const { titles, companies } = getLookupStrings(item);
                    return (
                      <tr key={requestId || index} className="border-b border-gray-900 align-top">
                        <td className="py-2 pr-4 text-gray-300">{item.sourceType === 'test_drive' ? 'Test Drive' : 'Website'}</td>
                        <td className="py-2 pr-4 text-gray-300">{item.requestName || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{item.requestCompany || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{item.requesterEmail || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{titles || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{companies || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">
                          <input
                            value={draft.emailSentDate}
                            onChange={(e) =>
                              setWebsiteDrafts((prev) => ({
                                ...prev,
                                [requestId]: { ...draft, emailSentDate: e.target.value },
                              }))
                            }
                            placeholder="YYYY-MM-DD or timestamp"
                            className="w-52 px-2 py-1 bg-gray-900 border border-gray-800 rounded text-white"
                          />
                        </td>
                        <td className="py-2 pr-4 text-gray-300">
                          <input
                            value={draft.pdfUrl}
                            onChange={(e) =>
                              setWebsiteDrafts((prev) => ({
                                ...prev,
                                [requestId]: { ...draft, pdfUrl: e.target.value },
                              }))
                            }
                            placeholder="https://...pdf"
                            className="w-72 px-2 py-1 bg-gray-900 border border-gray-800 rounded text-white"
                          />
                        </td>
                        <td className="py-2 pr-4 text-gray-300">
                          <button
                            onClick={() => saveWebsiteRequest(item)}
                            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-white text-xs"
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredWebsiteRequests.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={9}>
                        No website requests loaded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-800">
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">Endpoint</th>
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Search ID</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((item, index) => (
                    <tr key={item.createdAt ?? index} className="border-b border-gray-900 align-top">
                      <td className="py-2 pr-4 text-gray-300">{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{item.endpoint ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{item.accountId ?? item.account_id ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{item.searchId ?? item.search_id ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">{item.responseStatus ?? item.message ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-300">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-blue-400">View</summary>
                          <pre className="mt-2 p-2 bg-gray-900 border border-gray-800 rounded text-gray-300 max-h-64 overflow-auto">{JSON.stringify(item, null, 2)}</pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={6}>
                        No logs loaded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
  const normalizeWebsiteRequest = (item: WebsiteRequest, sourceType: 'website' | 'test_drive'): WebsiteRequest => {
    if (sourceType === 'website') {
      return { ...item, sourceType };
    }
    const searches = Array.isArray(item.searches) ? item.searches : [];
    const mappedLookups = searches
      .map((row) => {
        const query = row && typeof row.query === 'object' ? (row.query as Record<string, unknown>) : (row as Record<string, unknown>);
        const companyName = String(query?.companyName ?? query?.company ?? '').trim();
        const title = String(query?.title ?? '').trim();
        const fullName = String(query?.name ?? query?.fullName ?? '').trim();
        const seniority = String(query?.seniority ?? '').trim();
        const department = String(query?.department ?? '').trim();
        const industry = String(query?.industry ?? '').trim();
        const employeeSize = String(query?.employeeSize ?? '').trim();
        return { companyName, title, fullName, seniority, department, industry, employeeSize };
      })
      .filter((row) =>
        row.companyName ||
        row.title ||
        row.fullName ||
        row.seniority ||
        row.department ||
        row.industry ||
        row.employeeSize
      );
    const reports = Array.isArray(item.reports) ? item.reports : [];
    const firstReportUrl =
      String(item.reportPdfUrl ?? '').trim() ||
      String((reports.find((r) => String((r as any)?.status ?? '') === 'generated') as any)?.reportAccessUrl ?? '').trim() ||
      String((reports.find((r) => String((r as any)?.status ?? '') === 'generated') as any)?.pdfUrl ?? '').trim();
    return {
      ...item,
      sourceType,
      requestName: String(item.requestName ?? item.requesterName ?? '').trim() || null,
      requestCompany: String(item.requestCompany ?? item.requesterCompany ?? '').trim() || null,
      emailSentDate: String(item.emailSentDate ?? item.reportSentDate ?? '').trim(),
      pdfUrl: firstReportUrl,
      lookups: mappedLookups,
    };
  };
